import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../../utils/apiConfig';
import { openInViewerWindow } from '../../utils/viewerWindow';
import CustomDatePicker from '../CustomDatePicker';
import { usePatientContext } from '../../contexts/PatientContext';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TableSortLabel,
  CircularProgress,
  Tooltip,
  Menu,
  ListItemIcon
} from '@mui/material';
import { 
  Cancel as CancelIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  LocalHospital as ModalityIcon,
  Male,
  Female,
  TransgenderOutlined,
  FormatListBulleted as ListIcon,
  Close as CloseIcon,
  Search,
  PictureAsPdf as PdfIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { createApiUrl } from '../../utils/apiConfig';
import { getAuthHeaders } from '../../utils/authUtils';
import { 
  loadFormatConfig, 
  formatDateSync, 
  formatDateTimeSync, 
  formatDateWithAgeSync 
} from '../../utils/dateTimeFormat';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import debounce from 'lodash/debounce';
import { useSnackbar } from '../CentralSnackbar';
import { useWebSocket } from '../../contexts/WebSocketContext';
import PDFPreviewDialog from './PDFPreviewDialog';
import PDFPreviewDialogStructured from './PDFPreviewDialogStructured';
import PriorReportTextView from './PriorReportTextView';
import AISummaryDialog from './AISummaryDialog';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsIcon from '@mui/icons-material/Settings';

// Konstanten für DataGrid
const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 56;
/** Dashboard-Widget: Tabelleninhalt kompakter (Layout + Darstellung, kein transform-„Leerraum“) */
const WIDGET_TABLE_ZOOM = 0.7;

/** Vollbild-Approval: nur der Seiteninhalt unter der App Bar (nicht die App Bar) */
const APPROVAL_PAGE_CONTENT_ZOOM = 0.8;

const ApprovalWorkflow = ({ variant = 'full', widgetBodyHeight = null }) => {
  const isNewExamsWidget = variant === 'newExamsWidget';
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { showSuccess, showError, showWarning } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setContext, clearContext } = usePatientContext();
  const { newMessage } = useWebSocket();
  const [newExams, setNewExams] = useState([]);
  const [priorStudies, setPriorStudies] = useState([]);
  const [priorStudiesPatientId, setPriorStudiesPatientId] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [aiSummaryAnchor, setAiSummaryAnchor] = useState(null);
  const [aiSummaryMenuAnchor, setAiSummaryMenuAnchor] = useState(null);
  const [aiUserPrompts, setAiUserPrompts] = useState([]);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [selectedAiPrompt, setSelectedAiPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('NEW');
  const [searchText, setSearchText] = useState(''); // Neuer State für Suche
  const [dateFilter, setDateFilter] = useState('');
  const [modalityFilter, setModalityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at'); // Sortierung nach Spalte
  const [sortDirection, setSortDirection] = useState('desc'); // Sortierrichtung
  const [lockedExams, setLockedExams] = useState({}); // { exam_id: { opened_by, user_name } }

  // State für Voruntersuchungen Filter und Sortierung
  const [priorStudiesSearchText, setPriorStudiesSearchText] = useState('');
  const [priorStudiesModalityFilter, setPriorStudiesModalityFilter] = useState('all');
  const [priorStudiesSortBy, setPriorStudiesSortBy] = useState('study_date');
  const [priorStudiesSortDirection, setPriorStudiesSortDirection] = useState('desc');

  // State für Viewer-Konfiguration
  const [viewerConfig, setViewerConfig] = useState({
    web: { base_url: '', test_study_uid: '' },
    client: { base_url: '', test_study_uid: '' }
  });

  // State für editierbare Felder
  const [editableComment, setEditableComment] = useState('');
  const [editableScanProtocol, setEditableScanProtocol] = useState('');

  // State für Untersuchungsprotokoll Dialog
  const [scanProtocolDialogOpen, setScanProtocolDialogOpen] = useState(false);
  const [scanProtocolsByRegion, setScanProtocolsByRegion] = useState({});
  const [selectedRegion, setSelectedRegion] = useState(null);

  // State für Status-Änderungs-Dialog
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusChangeReason, setStatusChangeReason] = useState('');

  // AbortController für Request Cancellation
  const abortControllerRef = useRef(null);
  const currentExamIdRef = useRef(null);
  const [isLoadingExamDetails, setIsLoadingExamDetails] = useState(false);
  const examSelectionTimeoutRef = useRef(null);

  // Spalten-Management States
  const [approvalSettings, setApprovalSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // New Exams DataGrid States
  const [newExamsColumnWidths, setNewExamsColumnWidths] = useState({});
  const [newExamsColumnOrder, setNewExamsColumnOrder] = useState([]);
  const [newExamsColumnVisibility, setNewExamsColumnVisibility] = useState({});
  
  // Prior Studies DataGrid States
  const [priorStudiesColumnWidths, setPriorStudiesColumnWidths] = useState({});
  const [priorStudiesColumnOrder, setPriorStudiesColumnOrder] = useState([]);
  const [priorStudiesColumnVisibility, setPriorStudiesColumnVisibility] = useState({});

  // State für PDF Preview
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewStructuredOpen, setPdfPreviewStructuredOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  
  // State für Prior Report Text View
  const [showPriorReportText, setShowPriorReportText] = useState(false);
  const [selectedPriorStudy, setSelectedPriorStudy] = useState(null);
  
  // Ref für DataGrid API und Row-Scroll-Position
  const priorStudiesApiRef = useGridApiRef();
  const lastOpenedPriorStudyIdRef = useRef(null);
  
  // Tracking ob der Viewer explizit geöffnet wurde (Prior Study Doppelklick / Voice)
  const viewerWasOpenedRef = useRef(false);
  
  // Refs für Voice Control - Eingabefelder fokussieren
  const commentFieldRef = useRef(null);
  const protocolFieldRef = useRef(null);

  // Debounced Settings Save
  const debouncedSaveApprovalSettings = useCallback(debounce((settings) => {
    saveApprovalSettings(settings);
  }, 1000), []);

  useEffect(() => {
    fetchCurrentUser();
    loadFormatConfig();

    const loadAiPrompts = async () => {
      try {
        const response = await fetch(createApiUrl('profile/ai-settings'), {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setAiUserPrompts(data.ai_settings?.prompts || []);
        }
      } catch (e) {
        console.error('Error loading AI prompts:', e);
      }
    };
    loadAiPrompts();
  }, []);

  const handleAiSummaryMenuOpen = (event) => {
    setAiSummaryMenuAnchor(event.currentTarget);
  };

  const handleAiSummaryMenuClose = () => {
    setAiSummaryMenuAnchor(null);
  };

  const handleAiSummarySelect = (prompt) => {
    setAiSummaryMenuAnchor(null);
    setSelectedAiPrompt(prompt);
    setAiSummaryOpen(true);
  };

  // WebSocket Listener für Lock-Updates
  useEffect(() => {
    if (!newMessage) return;
    
    const { type, report_id, exam_id, opened_by, opened_by_name } = newMessage;
    
    if (type === 'exam_opened' && exam_id) {
      console.log('[LOCK] Exam geöffnet:', exam_id, 'von', opened_by_name);
      setLockedExams(prev => ({
        ...prev,
        [exam_id]: { opened_by, user_name: opened_by_name }
      }));
    } else if (type === 'exam_closed' && exam_id) {
      console.log('[LOCK] Exam geschlossen:', exam_id);
      setLockedExams(prev => {
        const updated = { ...prev };
        delete updated[exam_id];
        return updated;
      });
    }
  }, [newMessage]);

  // Voice Control: Status-Filter per Sprachbefehl setzen
  useEffect(() => {
    const handleVoiceStatusFilter = (event) => {
      const { filter } = event.detail;
      console.log('[VOICE] 🎤 Status-Filter Event empfangen:', filter);
      console.log('[VOICE] 🔄 Setze Filter von', statusFilter, 'auf', filter);
      setStatusFilter(filter);
    };

    console.log('[VOICE] 👂 Event Listener registriert für voiceSetStatusFilter');
    window.addEventListener('voiceSetStatusFilter', handleVoiceStatusFilter);
    return () => {
      console.log('[VOICE] 🔇 Event Listener entfernt');
      window.removeEventListener('voiceSetStatusFilter', handleVoiceStatusFilter);
    };
  }, [statusFilter]);

  // Voice Control: Modalitäten-Filter per Sprachbefehl setzen
  useEffect(() => {
    const handleVoiceModalityFilter = (event) => {
      const { modality } = event.detail;
      console.log('[VOICE] 🏥 Modalitäten-Filter Event empfangen:', modality);
      console.log('[VOICE] 🔄 Setze Modalität von', modalityFilter, 'auf', modality);
      setModalityFilter(modality);
    };

    console.log('[VOICE] 👂 Event Listener registriert für voiceSetModalityFilter');
    window.addEventListener('voiceSetModalityFilter', handleVoiceModalityFilter);
    return () => {
      window.removeEventListener('voiceSetModalityFilter', handleVoiceModalityFilter);
    };
  }, [modalityFilter]);

  // Voice Control: Datums-Filter per Sprachbefehl setzen
  useEffect(() => {
    const handleVoiceDateFilter = (event) => {
      const { date, relative } = event.detail;
      console.log('[VOICE] 📅 Datums-Filter Event empfangen:', relative, '→', date);
      console.log('[VOICE] 🔄 Setze Datum von', dateFilter, 'auf', date);
      setDateFilter(date);
    };

    console.log('[VOICE] 👂 Event Listener registriert für voiceSetDateFilter');
    window.addEventListener('voiceSetDateFilter', handleVoiceDateFilter);
    return () => {
      window.removeEventListener('voiceSetDateFilter', handleVoiceDateFilter);
    };
  }, [dateFilter]);

  // State für Voice-gesteuerte Vorbefund-Suche
  const [voicePriorStudyAction, setVoicePriorStudyAction] = useState(null);

  // Voice Control: Vorbefund-Suche Event empfangen
  useEffect(() => {
    const handleVoicePriorStudySearch = (event) => {
      const { action } = event.detail;
      console.log('[VOICE] 📋 Vorbefund-Suche Event empfangen:', action);
      setVoicePriorStudyAction(action);
    };

    console.log('[VOICE] 👂 Event Listener registriert für voicePriorStudySearch');
    window.addEventListener('voicePriorStudySearch', handleVoicePriorStudySearch);
    return () => {
      window.removeEventListener('voicePriorStudySearch', handleVoicePriorStudySearch);
    };
  }, []);

  // Voice Control: Feld-Fokus per Sprachbefehl setzen
  useEffect(() => {
    const handleVoiceFieldFocus = (event) => {
      const { field } = event.detail;
      console.log('[VOICE] 📝 Feld-Fokus Event empfangen:', field);
      
      // Fokussiere das entsprechende Feld
      // inputRef bei MUI TextField zeigt direkt auf das textarea-Element
      if (field === 'comment' && commentFieldRef.current) {
        console.log('[VOICE] ✅ Fokussiere Kommentarfeld');
        const textarea = commentFieldRef.current;
        textarea.focus();
        // Cursor ans Ende setzen
        if (textarea.setSelectionRange) {
          const length = textarea.value?.length || 0;
          textarea.setSelectionRange(length, length);
        }
      } else if (field === 'protocol' && protocolFieldRef.current) {
        console.log('[VOICE] ✅ Fokussiere Protokollfeld');
        const textarea = protocolFieldRef.current;
        textarea.focus();
        // Cursor ans Ende setzen
        if (textarea.setSelectionRange) {
          const length = textarea.value?.length || 0;
          textarea.setSelectionRange(length, length);
        }
      } else {
        console.warn('[VOICE] ⚠️ Feld nicht gefunden oder kein Ref:', field);
      }
    };

    console.log('[VOICE] 👂 Event Listener registriert für voiceFieldFocus');
    window.addEventListener('voiceFieldFocus', handleVoiceFieldFocus);
    return () => {
      window.removeEventListener('voiceFieldFocus', handleVoiceFieldFocus);
    };
  }, []);

  // Voice Control: Dialog-Aktionen per Sprachbefehl
  useEffect(() => {
    const handleVoiceDialogAction = (event) => {
      const { action, value } = event.detail;
      console.log('[VOICE] 🗂️ Dialog-Aktion Event empfangen:', action, value);
      console.log('[VOICE] 📋 selectedExam:', selectedExam?.id, selectedExam?.modality);
      
      switch (action) {
        case 'openProtocolDialog':
          console.log('[VOICE] ✅ Öffne Protokoll-Dialog');
          // Direkte Prüfung statt Funktionsaufruf (um aktuellen State zu verwenden)
          if (selectedExam && selectedExam.modality) {
            console.log('[VOICE] 📂 Lade Protokolle für Modalität:', selectedExam.modality);
            fetchScanProtocols(selectedExam.modality);
            // Grammatik neu laden nach Dialog-Öffnung (mit Verzögerung für DOM-Update)
            setTimeout(() => {
              if (window.reloadGrammar) {
                console.log('[VOICE] 🔄 Lade Grammatik neu für Dialog-Elemente');
                window.reloadGrammar();
              }
            }, 500);
          } else {
            console.warn('[VOICE] ⚠️ Keine Untersuchung ausgewählt oder keine Modalität');
            showWarning(t('approval.dialog.selectExamFirst'));
          }
          break;
        case 'closeDialog':
          console.log('[VOICE] ✅ Schließe Dialog');
          setScanProtocolDialogOpen(false);
          setStatusChangeDialogOpen(false);
          break;
        case 'selectRegion':
          if (value && scanProtocolsByRegion[value]) {
            console.log('[VOICE] ✅ Wähle Region:', value);
            setSelectedRegion(value);
            // Dialog öffnen, falls noch nicht offen
            if (!scanProtocolDialogOpen) {
              console.log('[VOICE] 📂 Öffne Protokoll-Dialog für Region:', value);
              setScanProtocolDialogOpen(true);
            }
            // Grammatik neu laden für Protokolle der neuen Region
            setTimeout(() => {
              if (window.reloadGrammar) {
                window.reloadGrammar();
              }
            }, 100);
          } else {
            console.warn('[VOICE] ⚠️ Region nicht gefunden:', value, '| Verfügbar:', Object.keys(scanProtocolsByRegion));
          }
          break;
        case 'selectProtocol':
          if (value && selectedRegion && scanProtocolsByRegion[selectedRegion]) {
            const protocol = scanProtocolsByRegion[selectedRegion].find(
              p => p.name.toLowerCase() === value.toLowerCase() || 
                   p.name.toLowerCase().includes(value.toLowerCase())
            );
            if (protocol) {
              console.log('[VOICE] ✅ Wähle Protokoll:', protocol.name);
              // Dialog öffnen falls noch nicht offen, dann Protokoll auswählen
              if (!scanProtocolDialogOpen) {
                setScanProtocolDialogOpen(true);
                // Kurze Verzögerung damit Dialog rendert
                setTimeout(() => handleSelectScanProtocol(protocol), 100);
              } else {
                handleSelectScanProtocol(protocol);
              }
            } else {
              console.warn('[VOICE] ⚠️ Protokoll nicht gefunden:', value, '| Verfügbar:', scanProtocolsByRegion[selectedRegion]?.map(p => p.name));
            }
          } else {
            console.warn('[VOICE] ⚠️ Keine Region ausgewählt oder keine Protokolle verfügbar');
          }
          break;
        default:
          console.warn('[VOICE] ⚠️ Unbekannte Dialog-Aktion:', action);
      }
    };

    console.log('[VOICE] 👂 Event Listener registriert für voiceDialogAction');
    window.addEventListener('voiceDialogAction', handleVoiceDialogAction);
    return () => {
      window.removeEventListener('voiceDialogAction', handleVoiceDialogAction);
    };
  }, [scanProtocolsByRegion, selectedRegion, selectedExam, scanProtocolDialogOpen, t, showWarning]);

  // Schließe Exam Lock beim Unmount
  useEffect(() => {
    return () => {
      if (selectedExam && selectedExam.id) {
        fetch(createApiUrl(`exams/${selectedExam.id}/close`), {
          method: 'POST',
          headers: getAuthHeaders(),
          keepalive: true
        }).catch(err => console.error('[LOCK] Fehler beim Schließen des Exams (unmount):', err));
      }
    };
  }, [selectedExam]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(createApiUrl('auth/me'), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        // Nach dem Laden des Benutzers die gefilterten Untersuchungen laden
        fetchNewExamsForUser(userData.id);
        // Viewer-Konfiguration laden
        await loadViewerConfig();
      } else {
        showError(t('approval.error.userDataLoad'), 'ERR-APPROVAL-001');
        setLoading(false);
      }
    } catch (err) {
      showError(t('approval.error.networkUserData'), 'ERR-APPROVAL-002');
      setLoading(false);
    }
  };

  const loadViewerConfig = async () => {
    try {
      const response = await fetch(createApiUrl('profile/viewer-config'), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setViewerConfig(data.viewer_config);
      } else {
        console.error('Failed to load viewer config:', data.message);
      }
    } catch (error) {
      console.error('Error loading viewer config:', error);
    }
  };

  useEffect(() => {
    if (isNewExamsWidget) return undefined;
    // Main-Element finden und Styling anpassen
    const main = document.querySelector('main');
    if (main) {
      main.style.justifyContent = 'flex-start';
      main.style.alignItems = 'flex-start';
    }
    // Beim Verlassen zurücksetzen
    return () => {
      if (main) {
        main.style.justifyContent = '';
        main.style.alignItems = '';
      }
    };
  }, [isNewExamsWidget]);

  const fetchNewExams = async () => {
    try {
      setLoading(true);
      const response = await fetch(createApiUrl('new-exams/'), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewExams(data);
      } else {
        showError(t('approval.error.examLoad'), 'ERR-APPROVAL-003');
      }
    } catch (err) {
      showError(t('approval.error.networkData'), 'ERR-APPROVAL-004');
    } finally {
      setLoading(false);
    }
  };

  const fetchNewExamsForUser = async (userId) => {
    try {
      setLoading(true);
      const response = await fetch(createApiUrl(`new-exams/user/${userId}`), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 ApprovalWorkflow DEBUG - Raw API data from /new-exams/user/' + userId + ':', data.length, 'exams');
        console.log('🔍 Status values found:', [...new Set(data.map(exam => exam.status))]);
        console.log('🔍 Sample exams:', data.slice(0, 3).map(e => ({id: e.id, status: e.status, patient: e.patient_name})));
        setNewExams(data);
        
        // Check for URL parameters (Deep-Link from Chat)
        const examIdParam = searchParams.get('examId');
        if (examIdParam) {
          const examId = parseInt(examIdParam, 10);
          const targetExam = data.find(exam => exam.id === examId);
          
          if (targetExam) {
            // Set status filter to 'all' to ensure exam is visible
            setStatusFilter('all');
            
            // Select the exam after a short delay to ensure UI is ready
            setTimeout(() => {
              handleExamSelect(targetExam, { forceSelect: true });
              // Clear the URL parameter after selection
              setSearchParams({});
            }, 500);
          } else {
            console.warn('Exam with ID', examId, 'not found in approval workflow');
            // Clear the URL parameter
            setSearchParams({});
          }
        }
      } else {
        showError(t('approval.error.examLoad'), 'ERR-APPROVAL-005');
      }
    } catch (err) {
      showError(t('approval.error.networkData'), 'ERR-APPROVAL-006');
    } finally {
      setLoading(false);
    }
  };

  const fetchPriorStudies = async (patientId) => {
    try {
      const response = await fetch(createApiUrl(`prior-studies/?patient_id=${patientId}`), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter by patient_id since the API doesn't have this endpoint yet
        const filteredData = data.filter(study => study.patient_id === patientId);
        setPriorStudies(filteredData);
      } else {
        setPriorStudies([]);
      }
    } catch (err) {
      console.error('Error fetching prior studies:', err);
      setPriorStudies([]);
    }
  };

  // Viewer leeren (sendet leere Study Instance UID)
  const sendEmptyViewerRequest = useCallback(() => {
    // Nur leeren wenn der Viewer zuvor explizit geöffnet wurde
    if (!viewerWasOpenedRef.current) {
      console.log('[VIEWER] Viewer-Leerung übersprungen - Viewer wurde nicht aktiv geöffnet');
      return;
    }

    // Sichere Überprüfungen um Auth-Probleme zu vermeiden
    if (!currentUser || !currentUser.viewer_preference || !viewerConfig) {
      console.log('[VIEWER] Viewer-Leerung übersprungen - keine Konfiguration');
      return;
    }

    const userViewerType = currentUser.viewer_preference;
    const viewerSetting = viewerConfig[userViewerType];

    if (!viewerSetting) {
      console.log('[VIEWER] Viewer-Leerung übersprungen - keine Viewer-Einstellung');
      return;
    }

    let baseUrl = viewerSetting.base_url;

    if (!baseUrl) {
      console.warn(`[VIEWER] Keine base URL konfiguriert für Viewer: ${userViewerType}`);
      return;
    }

    // Leere Study Instance UID senden
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', '');
    console.log('[VIEWER] Sende leere UID an Viewer:', userViewerType);

    try {
      if (userViewerType === 'web') {
        if (!document.hidden) {
          openInViewerWindow(viewerUrl);
        }
      } else if (userViewerType === 'client') {
        // Für Client-Viewer: Sichere Methode verwenden
        const link = document.createElement('a');
        link.href = viewerUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('[VIEWER] Fehler beim Leeren des Viewers:', error);
    }
    viewerWasOpenedRef.current = false;
  }, [currentUser, viewerConfig]);

  // Aufräumen bei Patientenwechsel - schließt Vorbefund und leert Viewer
  const cleanupOnPatientChange = useCallback(() => {
    console.log('[CLEANUP] Räume auf bei Patientenwechsel...');
    // Vorbefund-Dialog schließen
    setShowPriorReportText(false);
    setSelectedPriorStudy(null);
    // Viewer leeren
    sendEmptyViewerRequest();
  }, [sendEmptyViewerRequest]);

  // Verbesserte handleExamSelect mit Debouncing und Race Condition Prevention
  /** @param {{ forceSelect?: boolean }} [options] forceSelect: kein Toggle-Abwählen (Deep-Link, Auto-Select, Strict Mode) */
  const handleExamSelect = useCallback(async (exam, options = {}) => {
    const forceSelect = options.forceSelect === true;
    // Deselektierung: Wenn bereits ausgewählt, dann deselektieren (nur bei normalem Zeilenklick)
    if (!forceSelect && selectedExam && selectedExam.id === exam.id) {
      // Aufräumen: Vorbefund schließen und Viewer leeren
      cleanupOnPatientChange();
      
      // Schließe Exam Lock
      try {
        const response = await fetch(createApiUrl(`exams/${exam.id}/close`), {
          method: 'POST',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          console.log('[LOCK] Exam geschlossen:', exam.id);
        }
      } catch (error) {
        console.error('[LOCK] Fehler beim Schließen des Exams:', error);
      }
      
      setSelectedExam(null);
      setPriorStudies([]);
      setPriorStudiesPatientId(null);
      setIsLoadingExamDetails(false);
      currentExamIdRef.current = null;
      clearContext(); // Clear patient context
      return;
    }

    // Schließe vorheriges Exam Lock wenn vorhanden
    if (selectedExam && selectedExam.id) {
      // Aufräumen: Vorbefund schließen und Viewer leeren
      cleanupOnPatientChange();
      
      try {
        await fetch(createApiUrl(`exams/${selectedExam.id}/close`), {
          method: 'POST',
          headers: getAuthHeaders()
        });
        console.log('[LOCK] Vorheriges Exam geschlossen:', selectedExam.id);
      } catch (error) {
        console.error('[LOCK] Fehler beim Schließen des vorherigen Exams:', error);
      }
    }

    // Öffne neues Exam Lock
    try {
      const response = await fetch(createApiUrl(`exams/${exam.id}/open`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        console.log('[LOCK] Exam geöffnet:', exam.id);
      } else if (response.status === 409) {
        const errorData = await response.json();
        showWarning(t('approval.messages.examLockedBy', { user: errorData.opened_by_name || 'Unbekannt' }));
        return;
      }
    } catch (error) {
      console.error('[LOCK] Fehler beim Öffnen des Exams:', error);
    }

    // Clear any pending selection timeout
    if (examSelectionTimeoutRef.current) {
      clearTimeout(examSelectionTimeoutRef.current);
    }

    // Cancelle alle laufenden Requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Setze Loading State
    setIsLoadingExamDetails(true);
    
    // KRITISCH: Prior Studies SOFORT leeren BEVOR neue Daten geladen werden
    setPriorStudies([]);
    setPriorStudiesPatientId(null);
    
    // Setze sofort die grundlegenden Daten
    setSelectedExam(exam);
    setEditableComment(exam.comment || '');
    setEditableScanProtocol(exam.scan_protocol || '');
    
    // Setze Patientenkontext für Chat
    setContext({
      type: 'approval',
      examId: exam.id,
      patientName: exam.patient_name || 'Unbekannt',
      patientId: exam.patient_id,
      studyDate: exam.study_date ? formatDateSync(exam.study_date) : '',
      modality: exam.modality || ''
    });
    
    // VOICE: Scanprotokolle vorladen für Sprachsteuerung
    if (exam.modality) {
      console.log('[VOICE] 📂 Lade Scanprotokolle vor für Modalität:', exam.modality);
      preloadScanProtocolsForVoice(exam.modality);
    }
    
    // Speichere die aktuelle Exam ID für Referenz
    currentExamIdRef.current = exam.id;
    
    // Debounce the actual data loading to prevent rapid-fire selections
    examSelectionTimeoutRef.current = setTimeout(() => {
      // Double-check that this exam is still selected
      if (currentExamIdRef.current === exam.id) {
        // Neuer AbortController für diese Selection
        abortControllerRef.current = new AbortController();
        loadExamDetails(exam, abortControllerRef.current.signal);
      }
    }, 100); // 100ms debounce
    
  }, [selectedExam, cleanupOnPatientChange]);

  // Separate Funktion für das Laden der Exam Details
  const loadExamDetails = async (exam, abortSignal) => {
    try {
      const examId = exam.id;
      
      // 1. Prior Studies laden (falls Patient ID vorhanden)
      if (exam.patient_id) {
        try {
          const response = await fetch(createApiUrl(`prior-studies/?patient_id=${exam.patient_id}`), {
            headers: getAuthHeaders(),
            signal: abortSignal
          });
          
          if (abortSignal.aborted) {
            return;
          }
          
          if (response.ok) {
            const studies = await response.json();
            
            // SECURITY: Prüfe nur Exam ID und Abort Status - Patient ID Response ist automatisch korrekt
            if (currentExamIdRef.current === examId && !abortSignal.aborted) {
              setPriorStudies(studies);
              setPriorStudiesPatientId(exam.patient_id);
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('❌ Fehler beim Laden der Prior Studies:', error);
          }
        }
      } else {
        if (currentExamIdRef.current === examId && !abortSignal.aborted) {
          setPriorStudies([]);
          setPriorStudiesPatientId(null);
        }
      }

      // 2. Untersuchungsprotokoll Vorschläge laden (nur für neue Untersuchungen ohne Protokoll)
      if (exam.study_description && exam.modality && !exam.scan_protocol && exam.status === 'NEW') {
        try {
          const response = await fetch(
            createApiUrl(`scan-protocols/suggestions?modality=${encodeURIComponent(exam.modality)}&study_description=${encodeURIComponent(exam.study_description)}`),
            {
              headers: getAuthHeaders(),
              signal: abortSignal
            }
          );
          
          if (abortSignal.aborted) {
            return;
          }
          
          if (response.ok) {
            const suggestions = await response.json();
            
            // Nur verarbeiten wenn noch dieselbe Untersuchung ausgewählt ist und noch kein Protokoll gesetzt wurde
            if (currentExamIdRef.current === examId && !abortSignal.aborted && !selectedExam?.scan_protocol) {
              await handleScanProtocolSuggestions(suggestions, examId, abortSignal);
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('❌ Fehler beim Laden der Untersuchungsprotokoll Vorschläge:', error);
          }
        }
      }

    } finally {
      // Loading State zurücksetzen (nur wenn nicht aborted)
      if (!abortSignal.aborted && currentExamIdRef.current === exam.id) {
        setIsLoadingExamDetails(false);
      }
    }
  };

  // Separate Funktion für Untersuchungsprotokoll Suggestions Handling
  const handleScanProtocolSuggestions = async (suggestions, examId, abortSignal) => {
    if (suggestions.auto_select) {
      // Automatische Auswahl wenn nur ein Protokoll passt
      const protocolText = `${suggestions.auto_select.name}\n${suggestions.auto_select.description || ''}`;
      
      // Setze im UI (nur wenn noch dieselbe Untersuchung und kein Protokoll bereits gesetzt)
      if (currentExamIdRef.current === examId && !abortSignal.aborted && !selectedExam?.scan_protocol) {
        setEditableScanProtocol(protocolText);
        
        // Speichere automatisch ausgewähltes Protokoll
        try {
          await handleSaveFieldSafe('scan_protocol', protocolText, examId, abortSignal);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('❌ Fehler beim automatischen Speichern des Untersuchungsprotokolls:', error);
            console.error('Details:', {
              examId: examId,
              protocolName: suggestions.auto_select.name,
              protocolText: protocolText
            });
          }
        }
      }
    } else if (suggestions.focus_region && suggestions.suggestions.length > 0) {
      // Lade alle Protokolle für Modalität und fokussiere auf vorgeschlagene Region
      try {
        const allProtocolsResponse = await fetch(createApiUrl(`scan-protocols/by-modality?modality=${encodeURIComponent(selectedExam?.modality)}`), {
          headers: getAuthHeaders(),
          signal: abortSignal
        });
        
        if (abortSignal.aborted) return;
        
        if (allProtocolsResponse.ok && currentExamIdRef.current === examId) {
          const allData = await allProtocolsResponse.json();
          setScanProtocolsByRegion(allData);
          setSelectedRegion(suggestions.focus_region);
          setScanProtocolDialogOpen(true);
        } else {
          console.error('Fehler beim Laden der Untersuchungsprotokolle:', allProtocolsResponse.status);
          showError(t('admin.reportingManagement.scanProtocols.loadError', 'Fehler beim Laden der Scanprotokolle'));
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Fehler beim Laden der Untersuchungsprotokolle für Region:', error);
          showError(t('admin.reportingManagement.scanProtocols.loadError', 'Fehler beim Laden der Scanprotokolle'));
        }
      }
    }
  };

  // Sichere Version von handleSaveField mit Race Condition Protection
  const handleSaveFieldSafe = async (fieldName, value, expectedExamId, abortSignal) => {
    try {
      const response = await fetch(createApiUrl(`new-exams/${expectedExamId}`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          [fieldName]: value
        }),
        signal: abortSignal
      });
      
      if (abortSignal?.aborted) return;
      
      if (response.ok) {
        const updatedExam = await response.json();
        
        // Nur State updaten wenn noch dieselbe Untersuchung ausgewählt ist
        if (currentExamIdRef.current === expectedExamId && !abortSignal?.aborted) {
          setSelectedExam(updatedExam);
          
          // Update auch in der Liste
          setNewExams(prevExams => 
            prevExams.map(exam => 
              exam.id === updatedExam.id ? updatedExam : exam
            )
          );
        }
      } else {
        const errorText = await response.text();
        console.error(`Failed to save ${fieldName}. Status: ${response.status}, Error:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || t('approval.error.saveField')}`);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(`Fehler beim Speichern von ${fieldName}:`, err);
        // Für automatische Speicherungen (z.B. Untersuchungsprotokoll) keine Alert anzeigen
        if (fieldName !== 'scan_protocol') {
          showError(t('approval.error.saveChanges'), 'ERR-APPROVAL-007');
        }
        throw err;
      }
    }
  };

  // Originale handleSaveField für manuelle Saves (ohne Exam ID check)
  const handleSaveField = async (fieldName, value) => {
    return handleSaveFieldSafe(fieldName, value, selectedExam?.id);
  };

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (examSelectionTimeoutRef.current) {
        clearTimeout(examSelectionTimeoutRef.current);
      }
    };
  }, []);

  // VOICE: Scanprotokolle vorladen für Sprachsteuerung (ohne Dialog zu öffnen)
  const preloadScanProtocolsForVoice = async (modality) => {
    try {
      console.log('[VOICE] 🔄 Preload Scanprotokolle für:', modality);
      const response = await fetch(createApiUrl(`scan-protocols/by-modality?modality=${encodeURIComponent(modality)}`), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setScanProtocolsByRegion(data);
        
        // Erste Region vorauswählen
        const regions = Object.keys(data);
        if (regions.length > 0) {
          const sortedRegions = regions.sort((a, b) => {
            const minIndexA = Math.min(...data[a].map(p => p.index || 0));
            const minIndexB = Math.min(...data[b].map(p => p.index || 0));
            return minIndexA - minIndexB;
          });
          setSelectedRegion(sortedRegions[0]);
        }
        
        console.log('[VOICE] ✅ Scanprotokolle vorgeladen:', Object.keys(data).length, 'Regionen');
        
        // Grammatik neu laden, nachdem DOM aktualisiert wurde
        setTimeout(() => {
          if (window.reloadGrammar) {
            console.log('[VOICE] 🔄 Lade Grammatik neu mit vorgeladenen Protokollen');
            window.reloadGrammar();
          }
        }, 300);
      }
    } catch (error) {
      console.error('[VOICE] Fehler beim Vorladen der Scanprotokolle:', error);
    }
  };

  // Fetch Untersuchungsprotokolle nach Modalität
  const fetchScanProtocols = async (modality) => {
    try {
      const response = await fetch(createApiUrl(`scan-protocols/by-modality?modality=${encodeURIComponent(modality)}`), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setScanProtocolsByRegion(data);
        // Erste Region automatisch auswählen (nach Index sortiert)
        const regions = Object.keys(data)
          .sort((a, b) => {
            const minIndexA = Math.min(...data[a].map(p => p.index || 0));
            const minIndexB = Math.min(...data[b].map(p => p.index || 0));
            // Bei gleichem Index: alphabetisch sortieren
            if (minIndexA === minIndexB) {
              return a.localeCompare(b);
            }
            return minIndexA - minIndexB;
          });
        if (regions.length > 0) {
          setSelectedRegion(regions[0]);
        }
        setScanProtocolDialogOpen(true);
      } else {
        console.error('Fehler beim Laden der Untersuchungsprotokolle:', response.status);
        showError(t('admin.reportingManagement.scanProtocols.loadError', 'Fehler beim Laden der Scanprotokolle'));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Untersuchungsprotokolle:', err);
      showError(t('admin.reportingManagement.scanProtocols.loadError', 'Fehler beim Laden der Scanprotokolle'));
    }
  };

  // Untersuchungsprotokoll auswählen
  const handleSelectScanProtocol = (protocol) => {
    const protocolText = `${protocol.name}\n${protocol.description}`;
    setEditableScanProtocol(protocolText);
    handleSaveField('scan_protocol', protocolText);
    setScanProtocolDialogOpen(false);
  };

  // Dialog öffnen für Untersuchungsprotokoll Auswahl
  const handleOpenScanProtocolDialog = () => {
    if (selectedExam && selectedExam.modality) {
      fetchScanProtocols(selectedExam.modality);
    } else {
      showWarning(t('approval.dialog.selectExamFirst'));
    }
  };

  // Status-Änderungs-Dialog öffnen
  const handleOpenStatusChangeDialog = () => {
    setNewStatus(selectedExam?.status || 'NEW');
    setStatusChangeReason('');
    setStatusChangeDialogOpen(true);
  };

  // Status-Änderung speichern
  const handleSaveStatusChange = async () => {
    if (!selectedExam || !statusChangeReason.trim()) {
      showWarning(t('approval.dialog.reasonRequired'));
      return;
    }

    try {
      const response = await fetch(createApiUrl(`new-exams/${selectedExam.id}/change-status`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          reason_for_status_change: statusChangeReason
        })
      });

      if (response.ok) {
        const updatedExam = await response.json();
        
        // Bei Statusänderung zu Approved oder Rejected: Zurück zur Ausgangslage
        if (newStatus === 'Approved' || newStatus === 'Rejected') {
          // Aufräumen: Vorbefund schließen und Viewer leeren
          cleanupOnPatientChange();
          
          setSelectedExam(null);
          setPriorStudies([]);
          setPriorStudiesPatientId(null);
          setIsLoadingExamDetails(false);
          currentExamIdRef.current = null;
          
          // Auto-select next patient
          if (currentUser) {
            try {
              const examsResponse = await fetch(createApiUrl(`new-exams/user/${currentUser.id}`), {
                headers: getAuthHeaders()
              });
              
              if (examsResponse.ok) {
                const examsData = await examsResponse.json();
                setNewExams(examsData);
                
                const newExamsFiltered = examsData.filter(exam => 
                  exam.status === statusFilter || statusFilter === 'all'
                );
                
                if (newExamsFiltered.length > 0) {
                  console.log('[AUTO-SELECT] Wähle nächsten Patienten nach Status-Änderung');
                  setTimeout(() => {
                    handleExamSelect(newExamsFiltered[0], { forceSelect: true });
                  }, 100);
                }
              }
            } catch (fetchErr) {
              console.error('Fehler beim Laden der nächsten Exams:', fetchErr);
            }
          }
        } else {
          // Bei Statusänderung auf New: Untersuchung weiterhin ausgewählt lassen
          setSelectedExam(updatedExam);
          
          // Refresh the filtered list if currentUser is available
          if (currentUser) {
            fetchNewExamsForUser(currentUser.id);
          }
        }
        
        // Dialog schließen
        setStatusChangeDialogOpen(false);
        setStatusChangeReason('');
      } else {
        showError(t('approval.error.statusChange'), 'ERR-APPROVAL-008');
      }
    } catch (err) {
      console.error('Fehler beim Status ändern:', err);
      showError(t('approval.error.statusChange'), 'ERR-APPROVAL-009');
    }
  };

  const handleApprove = async () => {
    if (!selectedExam) return;
    
    // Merke aktuellen Index in der gefilterten Liste für Auto-Selektion des nächsten
    const currentExamId = selectedExam.id;
    
    try {
      const response = await fetch(createApiUrl(`new-exams/${selectedExam.id}/approve`), {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const updatedExam = await response.json();
        
        // Aufräumen: Vorbefund schließen und Viewer leeren
        cleanupOnPatientChange();
        
        // Nach dem Genehmigen: Zurück zur Ausgangslage (keine Untersuchung ausgewählt)
        setSelectedExam(null);
        setPriorStudies([]);
        setPriorStudiesPatientId(null);
        setIsLoadingExamDetails(false);
        currentExamIdRef.current = null;
        
        // Refresh the filtered list and auto-select next patient
        if (currentUser) {
          // Lade neue Daten und wähle nächsten Patienten
          try {
            const examsResponse = await fetch(createApiUrl(`new-exams/user/${currentUser.id}`), {
              headers: getAuthHeaders()
            });
            
            if (examsResponse.ok) {
              const examsData = await examsResponse.json();
              setNewExams(examsData);
              
              // Finde nächsten NEW-Status Patienten (nur NEW wird in der Liste angezeigt bei statusFilter === 'NEW')
              const newExamsFiltered = examsData.filter(exam => 
                exam.status === statusFilter || statusFilter === 'all'
              );
              
              if (newExamsFiltered.length > 0) {
                // Wähle den ersten verbleibenden Patienten
                console.log('[AUTO-SELECT] Wähle nächsten Patienten nach Genehmigung');
                setTimeout(() => {
                  handleExamSelect(newExamsFiltered[0], { forceSelect: true });
                }, 100);
              }
            }
          } catch (fetchErr) {
            console.error('Fehler beim Laden der nächsten Exams:', fetchErr);
          }
        }
      }
    } catch (err) {
      console.error('Fehler beim Genehmigen:', err);
    }
  };

  const handleReject = async () => {
    if (!selectedExam) return;
    
    // Merke aktuellen Index in der gefilterten Liste für Auto-Selektion des nächsten
    const currentExamId = selectedExam.id;
    
    try {
      const response = await fetch(createApiUrl(`new-exams/${selectedExam.id}/reject`), {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const updatedExam = await response.json();
        
        // Aufräumen: Vorbefund schließen und Viewer leeren
        cleanupOnPatientChange();
        
        // Nach dem Ablehnen: Zurück zur Ausgangslage (keine Untersuchung ausgewählt)
        setSelectedExam(null);
        setPriorStudies([]);
        setPriorStudiesPatientId(null);
        setIsLoadingExamDetails(false);
        currentExamIdRef.current = null;
        
        // Refresh the filtered list and auto-select next patient
        if (currentUser) {
          // Lade neue Daten und wähle nächsten Patienten
          try {
            const examsResponse = await fetch(createApiUrl(`new-exams/user/${currentUser.id}`), {
              headers: getAuthHeaders()
            });
            
            if (examsResponse.ok) {
              const examsData = await examsResponse.json();
              setNewExams(examsData);
              
              // Finde nächsten NEW-Status Patienten (nur NEW wird in der Liste angezeigt bei statusFilter === 'NEW')
              const newExamsFiltered = examsData.filter(exam => 
                exam.status === statusFilter || statusFilter === 'all'
              );
              
              if (newExamsFiltered.length > 0) {
                // Wähle den ersten verbleibenden Patienten
                console.log('[AUTO-SELECT] Wähle nächsten Patienten nach Ablehnung');
                setTimeout(() => {
                  handleExamSelect(newExamsFiltered[0], { forceSelect: true });
                }, 100);
              }
            }
          } catch (fetchErr) {
            console.error('Fehler beim Laden der nächsten Exams:', fetchErr);
          }
        }
      }
    } catch (err) {
      console.error('Fehler beim Ablehnen:', err);
    }
  };

  // Verwende zentrale Formatierungsfunktionen
  const formatDate = formatDateSync;
  const formatDateTime = formatDateTimeSync;
  const formatDateWithAge = formatDateWithAgeSync;

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW': return 'info';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  // CSS-Klassen für Approval Status Chips
  const getApprovalStatusCssClass = (status) => {
    switch (status) {
      case 'NEW':
        return 'status-new';
      case 'APPROVED':
        return 'status-approved';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return 'status-new'; // Fallback
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'NEW': return t('approval.status.new');
      case 'APPROVED': return t('approval.status.approved');
      case 'REJECTED': return t('approval.status.rejected');
      default: return status;
    }
  };

  // Funktion zum Behandeln der Spalten-Sortierung
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Funktion zum Sortieren der Untersuchungen
  const sortExams = (exams) => {
    return [...exams].sort((a, b) => {
      // Standard-Status-Priorisierung wenn keine Spalte aktiv sortiert ist
      if (!sortBy && statusFilter === 'all') {
        const statusOrder = { 'NEW': 1, 'APPROVED': 2, 'REJECTED': 3 };
        const statusA = statusOrder[a.status] || 4;
        const statusB = statusOrder[b.status] || 4;
        
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        // Bei gleichem Status nach Datum sortieren (neueste zuerst)
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }

      // Benutzerdefinierte Sortierung
      if (sortBy === 'status') {
        const statusOrder = { 'NEW': 1, 'APPROVED': 2, 'REJECTED': 3 };
        const statusA = statusOrder[a.status] || 4;
        const statusB = statusOrder[b.status] || 4;
        return sortDirection === 'asc' ? statusA - statusB : statusB - statusA;
      }

      let aValue, bValue;
      
      switch (sortBy) {
        case 'patient_name':
          aValue = (a.patient_name || '').toLowerCase();
          bValue = (b.patient_name || '').toLowerCase();
          break;
        case 'modality':
          aValue = (a.modality || '').toLowerCase();
          bValue = (b.modality || '').toLowerCase();
          break;
        case 'study_description':
          aValue = (a.study_description || '').toLowerCase();
          bValue = (b.study_description || '').toLowerCase();
          break;
        case 'referring_physician':
          aValue = (a.referring_physician || '').toLowerCase();
          bValue = (b.referring_physician || '').toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Funktion zum Filtern und Sortieren der Untersuchungen
  const getFilteredAndSortedExams = () => {
    let filtered = newExams;
    
    // Status-Filter
    if (statusFilter !== 'all') {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(exam => exam.status === statusFilter);
      console.log(`🔍 Status Filter: ${beforeFilter} → ${filtered.length} exams (Filter: "${statusFilter}")`);
    }
    
    // Modalitäts-Filter
    if (modalityFilter !== 'all') {
      filtered = filtered.filter(exam => exam.modality === modalityFilter);
    }

    // Datums-Filter
    if (dateFilter) {
      filtered = filtered.filter(exam => {
        if (!exam.observation_date) return false;
        // Konvertiere observation_date zu YYYY-MM-DD Format für Vergleich mit HTML5 date input
        const examDate = new Date(exam.observation_date);
        if (isNaN(examDate.getTime())) return false;
        const examDateString = examDate.toISOString().split('T')[0]; // YYYY-MM-DD
        return examDateString === dateFilter;
      });
    }
    
    // Such-Filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(exam => 
        (exam.patient_name || '').toLowerCase().includes(searchLower) ||
        (exam.patient_id || '').toLowerCase().includes(searchLower) ||
        (exam.modality || '').toLowerCase().includes(searchLower) ||
        (exam.study_description || '').toLowerCase().includes(searchLower) ||
        (exam.referring_physician || '').toLowerCase().includes(searchLower) ||
        (exam.status || '').toLowerCase().includes(searchLower)
      );
    }
    
    // Sortierung wird vom DataGrid übernommen
    return filtered;
  };

  // Funktionen für Prior Studies Sortierung und Filterung
  const handlePriorStudiesSort = (column) => {
    if (priorStudiesSortBy === column) {
      setPriorStudiesSortDirection(priorStudiesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setPriorStudiesSortBy(column);
      setPriorStudiesSortDirection('asc');
    }
  };

  const sortPriorStudies = (studies) => {
    return [...studies].sort((a, b) => {
      let aValue, bValue;
      
      switch (priorStudiesSortBy) {
        case 'study_date':
          aValue = new Date(a.study_date || 0);
          bValue = new Date(b.study_date || 0);
          return priorStudiesSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'modality':
          aValue = (a.modality || '').toLowerCase();
          bValue = (b.modality || '').toLowerCase();
          break;
        case 'study_description':
          aValue = (a.study_description || '').toLowerCase();
          bValue = (b.study_description || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return priorStudiesSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return priorStudiesSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Funktion zum Filtern und Sortieren der Voruntersuchungen (IMMER absteigend nach Datum)
  const getFilteredAndSortedPriorStudies = () => {
    // SICHERHEITS-CHECK: Nur Prior Studies anzeigen, wenn sie zur aktuellen Patient ID gehören
    if (!selectedExam || !priorStudiesPatientId || selectedExam.patient_id !== priorStudiesPatientId) {
      console.log('🖱️ DEBUG: Keine Prior Studies - Patient ID Mismatch');
      return []; // KEINE Prior Studies anzeigen bei Mismatch
    }
    
    let filtered = [...priorStudies]; // Erstelle eine Kopie des Arrays
    console.log('🖱️ DEBUG: Prior Studies vor Filterung:', filtered.length, filtered);
    console.log('🖱️ DEBUG: has_report Werte:', filtered.map(s => ({ 
      id: s.id, 
      new_exam_id: s.new_exam_id, 
      has_report: s.has_report,
      study_description: s.study_description?.substring(0, 30)
    })));
    
    // Modalitäts-Filter
    if (priorStudiesModalityFilter !== 'all') {
      filtered = filtered.filter(study => study.modality === priorStudiesModalityFilter);
    }
    
    // Such-Filter
    if (priorStudiesSearchText.trim()) {
      const searchLower = priorStudiesSearchText.toLowerCase();
      filtered = filtered.filter(study => 
        (study.modality || '').toLowerCase().includes(searchLower) ||
        (study.study_description || '').toLowerCase().includes(searchLower) ||
        (study.referring_physician || '').toLowerCase().includes(searchLower)
      );
    }
    
    // IMMER absteigend nach Datum sortieren
    const sorted = filtered.sort((a, b) => {
      const aDate = new Date(a.study_date || 0);
      const bDate = new Date(b.study_date || 0);
      return bDate - aDate; // Absteigend (neueste zuerst)
    });
    
    console.log('🖱️ DEBUG: Prior Studies nach Filterung:', sorted.length, sorted);
    return sorted;
  };

  // Eindeutige Modalitäten für Filter extrahieren
  const getUniqueModalities = () => {
    const modalities = [...new Set(priorStudies.map(study => study.modality).filter(Boolean))];
    return modalities.sort();
  };

  // Viewer für Voruntersuchung öffnen
  const handleOpenPriorStudyViewer = (study) => {
    if (!study.study_instance_uid) {
      showError(t('approval.error.noStudyUID'), 'ERR-APPROVAL-010');
      return;
    }

    if (!currentUser || !currentUser.viewer_preference) {
      showError(t('approval.error.viewerSettingUnavailable'), 'ERR-APPROVAL-011');
      return;
    }

    // Spezielle Modalitäten immer mit Webviewer öffnen
    const studyModality = study.modality ? study.modality.toUpperCase() : '';
    
    let userViewerType;
    let forceWebViewer = false;
    
    // ECG/EKG: Prüfe ob Modalität ECG oder EKG ENTHÄLT (z.B. ECG/SR, ECG\SR, EKG-Monitor)
    if (studyModality.includes('ECG') || studyModality.includes('EKG')) {
      forceWebViewer = true;
    }
    // OT/DOC: Exakte Übereinstimmung
    else if (studyModality === 'OT' || studyModality === 'DOC') {
      forceWebViewer = true;
    }
    
    if (forceWebViewer) {
      userViewerType = 'web'; // Forciere Webviewer für spezielle Modalitäten
    } else {
      userViewerType = currentUser.viewer_preference; // Verwende User-Präferenz für andere
    }
    
    const viewerSetting = viewerConfig[userViewerType];

    if (!viewerSetting) {
      // Spezielle Fehlermeldung für erzwungene Webviewer-Modalitäten
      if (forceWebViewer) {
        showError(t('approval.error.webViewerNotConfigured', { 
          modality: studyModality 
        }) || `Webviewer für Modalität "${studyModality}" ist nicht konfiguriert. Bitte konfigurieren Sie den Webviewer in der Serververwaltung.`, 'ERR-APPROVAL-012');
      } else {
        showError(t('approval.error.viewerConfigNotFound', { 
          viewerType: userViewerType, 
          available: Object.keys(viewerConfig).join(', ') 
        }), 'ERR-APPROVAL-013');
      }
      return;
    }

    let baseUrl = viewerSetting.base_url;

    if (!baseUrl) {
      showError(t('approval.error.noViewerURL', { viewerType: userViewerType }), 'ERR-APPROVAL-014');
      return;
    }

    // Ersetze den Platzhalter durch die tatsächliche Study Instance UID
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', study.study_instance_uid);

    try {
      if (userViewerType === 'web') {
        openInViewerWindow(viewerUrl);
      } else if (userViewerType === 'client') {
        window.location.href = viewerUrl;
      }
      viewerWasOpenedRef.current = true;
    } catch (error) {
      console.error('Error opening viewer:', error);
      showError(t('approval.error.viewerOpen'), 'ERR-APPROVAL-015');
    }
  };

  // Speichern der Approval-Einstellungen
  const saveApprovalSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      const response = await fetch(createApiUrl('profile/approval-settings'), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approval_settings: newSettings })
      });
      
      if (response.ok) {
        setApprovalSettings(newSettings);
      } else {
        console.error('Failed to save approval settings');
      }
    } catch (err) {
      console.error('Error saving approval settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // Handler für Spaltenbreiten-Änderungen - Neue Untersuchungen
  const handleNewExamsColumnWidthChange = useCallback((params) => {
    const { field, width } = params;
    setNewExamsColumnWidths(prev => {
      const newWidths = { ...prev, [field]: width };
      debouncedSaveApprovalSettings({
        ...approvalSettings,
        newExams: {
          ...approvalSettings?.newExams,
          columnWidths: newWidths
        }
      });
      return newWidths;
    });
  }, [approvalSettings]);

  // Handler für Spaltenbreiten-Änderungen - Voruntersuchungen
  const handlePriorStudiesColumnWidthChange = useCallback((params) => {
    const { field, width } = params;
    setPriorStudiesColumnWidths(prev => {
      const newWidths = { ...prev, [field]: width };
      debouncedSaveApprovalSettings({
        ...approvalSettings,
        priorStudies: {
          ...approvalSettings?.priorStudies,
          columnWidths: newWidths
        }
      });
      return newWidths;
    });
  }, [approvalSettings]);

  // Handler für Spaltenreihenfolge-Änderungen - Neue Untersuchungen
  const handleNewExamsColumnOrderChange = useCallback((params) => {
    const newOrder = params.map(col => col.field);
    setNewExamsColumnOrder(newOrder);
    saveApprovalSettings({
      ...approvalSettings,
      newExams: {
        ...approvalSettings?.newExams,
        columnOrder: newOrder
      }
    });
  }, [approvalSettings]);

  // Handler für Spaltenreihenfolge-Änderungen - Voruntersuchungen
  const handlePriorStudiesColumnOrderChange = useCallback((params) => {
    const newOrder = params.map(col => col.field);
    setPriorStudiesColumnOrder(newOrder);
    saveApprovalSettings({
      ...approvalSettings,
      priorStudies: {
        ...approvalSettings?.priorStudies,
        columnOrder: newOrder
      }
    });
  }, [approvalSettings]);

  // Handler für Sichtbarkeits-Änderungen - Neue Untersuchungen
  const handleNewExamsColumnVisibilityChange = useCallback((newVisibility) => {
    setNewExamsColumnVisibility(newVisibility);
    saveApprovalSettings({
      ...approvalSettings,
      newExams: {
        ...approvalSettings?.newExams,
        columnVisibility: newVisibility
      }
    });
  }, [approvalSettings]);

  // Handler für Sichtbarkeits-Änderungen - Voruntersuchungen
  const handlePriorStudiesColumnVisibilityChange = useCallback((newVisibility) => {
    setPriorStudiesColumnVisibility(newVisibility);
    saveApprovalSettings({
      ...approvalSettings,
      priorStudies: {
        ...approvalSettings?.priorStudies,
        columnVisibility: newVisibility
      }
    });
  }, [approvalSettings]);

  // Handler für Sortierung - Neue Untersuchungen
  const handleNewExamsSortModelChange = useCallback((params) => {
    if (params.length > 0) {
      const { field, sort } = params[0];
      setSortBy(field);
      setSortDirection(sort);
    } else {
      setSortBy('created_at');
      setSortDirection('desc');
    }
  }, []);

  // Handler für Sortierung - Voruntersuchungen (IMMER absteigend nach Datum)
  const handlePriorStudiesSortModelChange = useCallback((params) => {
    // Voruntersuchungen werden IMMER absteigend nach Datum sortiert
    // Andere Sortierungen werden ignoriert
    setPriorStudiesSortBy('study_date');
    setPriorStudiesSortDirection('desc');
  }, []);

  // Handler für Zeilenauswahl - Neue Untersuchungen
  const handleNewExamsRowClick = useCallback((params) => {
    handleExamSelect(params.row);
  }, [handleExamSelect]);

  useEffect(() => {
    if (isNewExamsWidget) return;
    if (loading) return;
    if (!newExams?.length) return;
    const preId = location.state?.preselectNewExamId;
    if (preId == null) return;
    const target = newExams.find((e) => e.id === preId || String(e.id) === String(preId));
    if (target) {
      setStatusFilter('all');
      handleExamSelect(target, { forceSelect: true });
    }
    navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei Deep-Link / Liste; handleExamSelect nicht in deps zyklen
  }, [isNewExamsWidget, loading, newExams, location.pathname, location.search, location.state, navigate]);

  // Handler für Zeilenauswahl - Voruntersuchungen
  const handlePriorStudiesRowDoubleClick = useCallback((params) => {
    handleOpenPriorStudyViewer(params.row);
  }, [handleOpenPriorStudyViewer]);

  // Hilfsfunktion: Normalisiert Studienbeschreibung für Vergleich
  const normalizeStudyDescription = useCallback((desc) => {
    if (!desc) return '';
    return desc
      .toLowerCase()
      .replace(/[^a-zäöüß0-9]/g, ' ')  // Sonderzeichen durch Leerzeichen ersetzen
      .replace(/\s+/g, ' ')             // Mehrfache Leerzeichen zusammenfassen
      .trim();
  }, []);

  // Hilfsfunktion: Extrahiert Schlüsselwörter aus Studienbeschreibung
  const extractKeywords = useCallback((desc) => {
    if (!desc) return [];
    const normalized = normalizeStudyDescription(desc);
    // Häufige Stopwörter entfernen
    const stopwords = ['und', 'mit', 'ohne', 'der', 'die', 'das', 'u', 'i', 'v', 'iv', 'km', 'nativ'];
    return normalized.split(' ').filter(word => word.length > 1 && !stopwords.includes(word));
  }, [normalizeStudyDescription]);

  // Hilfsfunktion: Berechnet Ähnlichkeits-Score zwischen zwei Studienbeschreibungen
  const calculateSimilarityScore = useCallback((desc1, desc2, modality1, modality2) => {
    const keywords1 = extractKeywords(desc1);
    const keywords2 = extractKeywords(desc2);
    
    if (keywords1.length === 0 || keywords2.length === 0) return 0;
    
    // Zähle übereinstimmende Keywords
    let matchCount = 0;
    for (const kw1 of keywords1) {
      for (const kw2 of keywords2) {
        // Exakte Übereinstimmung oder eines enthält das andere
        if (kw1 === kw2 || kw1.includes(kw2) || kw2.includes(kw1)) {
          matchCount++;
          break;
        }
        // Fuzzy: Ähnliche Anfänge (mind. 3 Zeichen)
        if (kw1.length >= 3 && kw2.length >= 3) {
          const minLen = Math.min(kw1.length, kw2.length);
          const prefix = Math.min(3, minLen);
          if (kw1.substring(0, prefix) === kw2.substring(0, prefix)) {
            matchCount += 0.5;
            break;
          }
        }
      }
    }
    
    // Basis-Score aus Keyword-Übereinstimmung
    let score = matchCount / Math.max(keywords1.length, keywords2.length);
    
    // Bonus für gleiche Modalität
    if (modality1 && modality2 && modality1.toUpperCase() === modality2.toUpperCase()) {
      score += 0.3;
    }
    
    return score;
  }, [extractKeywords]);

  // Voice Control: Vorbefund-Suche ausführen wenn Action gesetzt wird
  useEffect(() => {
    if (!voicePriorStudyAction) return;
    
    const action = voicePriorStudyAction;
    setVoicePriorStudyAction(null); // Reset
    
    if (!selectedExam) {
      console.warn('[VOICE] Keine Untersuchung ausgewählt');
      showWarning && showWarning(t('approval.voice.noExamSelected') || 'Bitte wählen Sie zuerst eine Untersuchung aus');
      return;
    }
    
    if (!priorStudies || priorStudies.length === 0) {
      console.warn('[VOICE] Keine Voruntersuchungen verfügbar');
      showWarning && showWarning(t('approval.voice.noPriorStudies') || 'Keine Voruntersuchungen verfügbar');
      return;
    }
    
    // Nur Voruntersuchungen mit Befund (has_report) berücksichtigen
    const studiesWithReport = priorStudies.filter(s => s.has_report);
    
    if (studiesWithReport.length === 0) {
      console.warn('[VOICE] Keine Voruntersuchungen mit Befund');
      showWarning && showWarning(t('approval.voice.noPriorReports') || 'Keine Voruntersuchungen mit Befund gefunden');
      return;
    }
    
    console.log('[VOICE] Aktuelle Untersuchung:', selectedExam.study_description, '| Modalität:', selectedExam.modality);
    console.log('[VOICE] Voruntersuchungen mit Befund:', studiesWithReport.length);
    
    let targetStudy = null;
    
    if (action === 'last') {
      // Finde den LETZTEN passenden Vorbefund (neuestes Datum, beste Übereinstimmung)
      
      // Sortiere nach Datum (neueste zuerst)
      const sortedByDate = [...studiesWithReport].sort((a, b) => 
        new Date(b.study_date) - new Date(a.study_date)
      );
      
      // Berechne Ähnlichkeits-Scores
      const withScores = sortedByDate.map(study => ({
        study,
        score: calculateSimilarityScore(
          selectedExam.study_description, 
          study.study_description,
          selectedExam.modality,
          study.modality
        )
      }));
      
      console.log('[VOICE] Ähnlichkeits-Scores:', withScores.map(s => ({
        desc: s.study.study_description,
        modality: s.study.modality,
        score: s.score.toFixed(2),
        date: s.study.study_date
      })));
      
      // Finde beste Übereinstimmung mit Score > 0.3
      const bestMatch = withScores.find(s => s.score > 0.3);
      
      if (bestMatch) {
        targetStudy = bestMatch.study;
        console.log('[VOICE] ✅ Bester Match gefunden:', targetStudy.study_description, 'Score:', bestMatch.score.toFixed(2));
      } else {
        // Fallback: Gleiche Modalität, neueste
        const sameModality = sortedByDate.find(s => 
          s.modality && selectedExam.modality && 
          s.modality.toUpperCase() === selectedExam.modality.toUpperCase()
        );
        
        if (sameModality) {
          targetStudy = sameModality;
          console.log('[VOICE] ✅ Fallback: Gleiche Modalität gefunden:', targetStudy.study_description);
        } else {
          // Letzter Fallback: Neueste Untersuchung überhaupt
          targetStudy = sortedByDate[0];
          console.log('[VOICE] ✅ Fallback: Neueste Untersuchung:', targetStudy.study_description);
        }
      }
    } else if (action === 'first') {
      // Älteste Untersuchung mit Befund
      const sortedByDate = [...studiesWithReport].sort((a, b) => 
        new Date(a.study_date) - new Date(b.study_date)
      );
      targetStudy = sortedByDate[0];
    } else if (action === 'next' || action === 'previous') {
      // Navigation in der Voruntersuchungen-Liste
      const sortedByDate = [...studiesWithReport].sort((a, b) => 
        new Date(b.study_date) - new Date(a.study_date)
      );
      
      // Finde aktuelle Position (basierend auf letztem geöffneten)
      const currentIndex = lastOpenedPriorStudyIdRef.current 
        ? sortedByDate.findIndex(s => s.id === lastOpenedPriorStudyIdRef.current)
        : -1;
      
      if (action === 'next') {
        targetStudy = sortedByDate[Math.min(currentIndex + 1, sortedByDate.length - 1)] || sortedByDate[0];
      } else {
        targetStudy = sortedByDate[Math.max(currentIndex - 1, 0)] || sortedByDate[sortedByDate.length - 1];
      }
    }
    
    if (targetStudy) {
      console.log('[VOICE] 🎯 Öffne Vorbefund:', targetStudy.study_description, '| Datum:', targetStudy.study_date);
      console.log('[VOICE] 📋 has_report:', targetStudy.has_report, '| new_exam_id:', targetStudy.new_exam_id);
      
      // Speichere ID für Navigation
      lastOpenedPriorStudyIdRef.current = targetStudy.id;
      
      // Scrolle zur Zeile
      if (priorStudiesApiRef.current) {
        const rowIndex = priorStudies.findIndex(s => s.id === targetStudy.id);
        if (rowIndex >= 0) {
          priorStudiesApiRef.current.scrollToIndexes({ rowIndex });
        }
      }
      
      // 1. Öffne die Bilder im Viewer
      console.log('[VOICE] 🖼️ Öffne Bilder im Viewer...');
      handleOpenPriorStudyViewer(targetStudy);
      
      // 2. Öffne den Befund-Text (wenn vorhanden)
      if (targetStudy.has_report && targetStudy.new_exam_id) {
        console.log('[VOICE] 📄 Öffne Befund-Text...');
        // Kurze Verzögerung, damit der Viewer zuerst öffnet
        setTimeout(() => {
          setSelectedPriorStudy(targetStudy);
          setShowPriorReportText(true);
        }, 300);
      }
    }
  }, [voicePriorStudyAction, selectedExam, priorStudies, showWarning, t, calculateSimilarityScore, handleOpenPriorStudyViewer]);

  // PDF Preview öffnen mit Template-Typ-Routing (wie im ReportEditor)
  const openPdfPreview = (pdfData) => {
    console.log('📄 Öffne PDF-Vorschau für Prior Study:', pdfData);
    
    // Template-Typ ermitteln
    const templateType = pdfData?.report?.report?.metadata?.template_type || 'F';
    console.log('🔍 Template-Typ für PDF-Preview:', templateType);
    
    setPdfData(pdfData);
    
    if (templateType === 'S') {
      // Strukturierter Befund
      console.log('📋 Öffne strukturierten PDF-Dialog');
      setPdfPreviewStructuredOpen(true);
    } else {
      // Freitext-Befund (Standard)
      console.log('📝 Öffne Freitext PDF-Dialog');
      setPdfPreviewOpen(true);
    }
  };

  // Callback: Zurück von Text-Ansicht zur Voruntersuchungsliste
  const handleBackFromPriorReportText = () => {
    setShowPriorReportText(false);
    setSelectedPriorStudy(null);
    // lastOpenedPriorStudyIdRef bleibt gesetzt für Scroll-Effekt
  };
  
  // Scrolle zur zuletzt geöffneten Row nach Rückkehr
  useEffect(() => {
    if (!showPriorReportText && lastOpenedPriorStudyIdRef.current && priorStudiesApiRef.current) {
      // Kleine Verzögerung, damit DataGrid vollständig gerendert ist
      const timer = setTimeout(() => {
        try {
          const rowId = lastOpenedPriorStudyIdRef.current;
          const allRows = priorStudiesApiRef.current.getAllRowIds();
          const rowIndex = allRows.indexOf(rowId);
          
          if (rowIndex !== -1) {
            // Scrolle zur Row und zentriere sie
            priorStudiesApiRef.current.scrollToIndexes({ rowIndex });
            console.log(`📜 Scrolle zur Prior Study Row ${rowId} (Index: ${rowIndex})`);
          }
          
          // Cleanup
          lastOpenedPriorStudyIdRef.current = null;
        } catch (error) {
          console.error('Fehler beim Scrollen zur Row:', error);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [showPriorReportText, priorStudiesApiRef]);
  
  // Callback: PDF anzeigen aus Text-Ansicht - Öffne Popup-Fenster
  const handleShowPdfFromTextView = (reportData) => {
    if (reportData && reportData.pdf_data) {
      // Öffne PDF Preview in separatem Popup-Fenster
      const tempKey = `pdf_preview_${reportData.report_id}_${Date.now()}`;
      localStorage.setItem(tempKey, JSON.stringify(reportData.pdf_data));
      
      // Optimale Größe für A4-Darstellung
      const width = 950;
      const height = Math.floor(window.screen.availHeight * 0.85);
      const left = Math.floor((window.screen.availWidth / 2 - width) / 2);
      const top = Math.floor((window.screen.availHeight - height) / 2);
      
      const newWindow = window.open(
        `/pdf-preview?key=${tempKey}`,
        `pdf_preview_${reportData.report_id}`,
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`
      );
      
      if (!newWindow) {
        showError(t('approval.error.popupBlocked'), 'ERR-APPROVAL-017');
        // Fallback: Öffne als Dialog
        openPdfPreview(reportData.pdf_data);
      } else {
        // Auto-cleanup nach 10 Minuten
        setTimeout(() => {
          try {
            localStorage.removeItem(tempKey);
          } catch (e) {
            console.warn('Cleanup-Fehler für PDF Preview Key:', e);
          }
        }, 10 * 60 * 1000);
      }
    } else {
      showError(t('approval.error.noPdfData'), 'ERR-APPROVAL-018');
    }
  };

  // Laden der Approval-Einstellungen
  useEffect(() => {
    const loadApprovalSettings = async () => {
      try {
        const response = await fetch(createApiUrl('profile/approval-settings'), {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setApprovalSettings(data.approval_settings || {});
          
          // Neue Untersuchungen Einstellungen
          if (data.approval_settings?.newExams) {
            setNewExamsColumnWidths(data.approval_settings.newExams.columnWidths || {});
            setNewExamsColumnOrder(data.approval_settings.newExams.columnOrder || []);
            setNewExamsColumnVisibility(data.approval_settings.newExams.columnVisibility || {});
          }
          
          // Voruntersuchungen Einstellungen
          if (data.approval_settings?.priorStudies) {
            setPriorStudiesColumnWidths(data.approval_settings.priorStudies.columnWidths || {});
            setPriorStudiesColumnOrder(data.approval_settings.priorStudies.columnOrder || []);
            setPriorStudiesColumnVisibility(data.approval_settings.priorStudies.columnVisibility || {});
          }
        }
      } catch (err) {
        console.error('Error loading approval settings:', err);
      }
    };
    loadApprovalSettings();
  }, []);

  // Handler: Öffne Prior Report Text View aus Icon-Klick
  const handleShowPriorReportFromIcon = (study, event) => {
    event.stopPropagation(); // Verhindere Row Click
    if (study && study.new_exam_id) {
      // Speichere die Row-ID für späteres Zurück-Scrollen
      const rowId = study.id || study.study_instance_uid;
      lastOpenedPriorStudyIdRef.current = rowId;
      
      setSelectedPriorStudy(study);
      setShowPriorReportText(true);
    }
  };

  // DataGrid Columns Definition - New Exams
  const newExamsColumns = useMemo(() => {
    const defaultColumns = [
      {
        field: 'status',
        headerName: t('approval.table.status'),
        width: 120,
        renderCell: (params) => {
          const status = params.value || 'NEW';
          const statusText = getStatusLabel(status);
          const cssClass = getApprovalStatusCssClass(status);
          
          return React.createElement('span', {
            className: `chip-approval-status ${cssClass}`
          }, statusText);
        }
      },
      {
        field: 'patient_name',
        headerName: t('approval.table.patient'),
        width: 200,
        renderCell: (params) => (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0, 
            justifyContent: 'center',
            height: '100%'
          }}>
            <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{params.value || '-'}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>ID: {params.row.patient_id || '-'}</Typography>
          </Box>
        )
      },
      {
        field: 'modality',
        headerName: t('approval.table.modality'),
        width: 100
      },
      {
        field: 'study_description',
        headerName: t('approval.table.studyDescription'),
        width: 250
      },
      {
        field: 'referring_physician',
        headerName: t('approval.table.referringPhysician'),
        width: 180
      },
      {
        field: 'observation_date',
        headerName: t('approval.table.date'),
        width: 120,
        valueFormatter: (value) => {
          if (!value) return '-';
          try {
            const date = new Date(value);
            return !isNaN(date.getTime()) ? date.toLocaleDateString('de-DE', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            }) : '-';
          } catch {
            return '-';
          }
        }
      },
      {
        field: 'created_at',
        headerName: t('approval.table.created'),
        width: 160,
        valueFormatter: (value) => {
          if (!value) return '-';
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('de-DE', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
              }) + ' ' + date.toLocaleTimeString('de-DE', { 
                hour: '2-digit', 
                minute: '2-digit' 
              });
            }
          } catch {
            return '-';
          }
          return '-';
        }
      }
    ];

    // Anwenden der benutzerdefinierten Reihenfolge
    if (newExamsColumnOrder.length > 0) {
      return newExamsColumnOrder.map(field => defaultColumns.find(col => col.field === field)).filter(Boolean);
    }

    return defaultColumns;
  }, [t, getStatusLabel, getStatusColor, getApprovalStatusCssClass, newExamsColumnOrder]);

  // DataGrid Columns Definition - Prior Studies
  const priorStudiesColumns = useMemo(() => {
    const defaultColumns = [
      {
        field: 'report',
        headerName: '', // Kein Text
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderHeader: () => (
          <DescriptionIcon sx={{ color: 'text.secondary' }} />
        ),
        renderCell: (params) => {
          const hasReport = params.row.has_report; // Backend prüft ob ein Report existiert
          if (!hasReport) return null;
          
          return (
            <IconButton
              size="small"
              onClick={(event) => handleShowPriorReportFromIcon(params.row, event)}
              sx={{
                color: 'var(--rd-primary)',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                }
              }}
            >
              <DescriptionIcon />
            </IconButton>
          );
        }
      },
      {
        field: 'study_date',
        headerName: t('approval.table.date'),
        width: 110,
        sortable: false, // Sortierung deaktiviert, da immer nach Datum absteigend sortiert
        valueFormatter: (value) => {
          if (!value) return '-';
          try {
            const date = new Date(value);
            return !isNaN(date.getTime()) ? date.toLocaleDateString('de-DE', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            }) : '-';
          } catch {
            return '-';
          }
        }
      },
      {
        field: 'modality',
        headerName: t('approval.table.modalityFull'),
        width: 100,
        sortable: false
      },
      {
        field: 'study_description',
        headerName: t('approval.table.description'),
        width: 380,
        sortable: false
      },
      {
        field: 'referring_physician',
        headerName: t('approval.table.referringPhysician'),
        width: 230,
        sortable: false
      }
    ];

    // Anwenden der benutzerdefinierten Reihenfolge
    if (priorStudiesColumnOrder.length > 0) {
      return priorStudiesColumnOrder.map(field => defaultColumns.find(col => col.field === field)).filter(Boolean);
    }

    return defaultColumns;
  }, [t, priorStudiesColumnOrder]);

  // DataGrid Locale Text
  const getGridLocale = () => {
    // Prüfe ob die Sprache Deutsch ist (de, de-DE, de-AT, etc.)
    if (i18n.language && i18n.language.startsWith('de')) {
      return {
        noRowsLabel: t('approval.table.noRows'),
        noResultsOverlayLabel: t('approval.table.noResults'),
        errorOverlayDefaultLabel: t('approval.table.error'),
        columnMenuSortAsc: t('approval.table.sortAsc'),
        columnMenuSortDesc: t('approval.table.sortDesc'),
        columnMenuFilter: t('approval.table.filter'),
        columnMenuHideColumn: t('approval.table.hideColumn'),
        columnMenuShowColumns: t('approval.table.showColumns'),
        columnMenuManageColumns: t('approval.table.manageColumns'),
        columnMenuUnsort: t('approval.table.unsort'),
        columnMenuFilterLabel: t('approval.table.filterLabel'),
        columnMenuFilterPlaceholder: t('approval.table.filterPlaceholder'),
        columnMenuFilterOk: t('approval.table.filterOk'),
        columnMenuFilterReset: t('approval.table.filterReset'),
        toolbarColumns: t('approval.table.toolbarColumns'),
        toolbarColumnsLabel: t('approval.table.toolbarColumnsLabel'),
        columnsPanelTextFieldLabel: t('approval.table.columnsPanelTextFieldLabel'),
        columnsPanelTextFieldPlaceholder: t('approval.table.columnsPanelTextFieldPlaceholder'),
        columnsPanelDragIconLabel: t('approval.table.columnsPanelDragIconLabel'),
        columnsPanelShowAllButton: t('approval.table.columnsPanelShowAllButton'),
        columnsPanelHideAllButton: t('approval.table.columnsPanelHideAllButton')
      };
    }
    
    // Englisch und andere Sprachen: Standard MUI Texte
    return {};
  };

  // DataGrid Styles mit Dark Mode Support
  /** @param {{ embedWidget?: boolean }} [options] */
  const getDataGridStyles = (options = {}) => {
    const { embedWidget = false } = options;
    const isDark = theme.palette.mode === 'dark';
    const bgColor = 'var(--rd-bg-paper)';
    const headerBg = 'var(--rd-bg-subtle)';
    const textColor = 'var(--rd-text-primary)';
    const borderColor = 'var(--rd-divider)';

    const rootLayout = embedWidget
      ? {
          flex: 1,
          minHeight: 0,
          width: '100%',
          height: '100%'
        }
      : {
          height: '100%',
          overflowY: 'auto'
        };

    return {
      ...rootLayout,
      backgroundColor: bgColor,
      borderRadius: 2,
      boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
      '--DataGrid-containerBackground': bgColor,
      '--DataGrid-pinnedBackground': bgColor,
      '& .MuiDataGrid-main': {
        backgroundColor: bgColor
      },
      '& .MuiDataGrid-virtualScroller': {
        backgroundColor: bgColor
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        backgroundColor: bgColor
      },
      '& .MuiDataGrid-virtualScrollerRenderZone': {
        backgroundColor: bgColor
      },
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: headerBg,
        color: textColor,
        fontWeight: 'bold',
        fontSize: '1rem',
        borderBottom: `1px solid ${borderColor}`,
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'normal',
        lineHeight: '1.2',
        wordBreak: 'break-word',
        textAlign: 'center',
      },
      '& .MuiDataGrid-row': {
        backgroundColor: bgColor,
        cursor: 'default',
        '&:nth-of-type(odd)': {
          backgroundColor: bgColor
        },
        '&:nth-of-type(even)': {
          backgroundColor: bgColor
        },
        '&:hover': {
          backgroundColor: 'color-mix(in srgb, var(--rd-primary) 4%, transparent)',
        },
        '&.Mui-selected': {
          backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
        },
        '&.Mui-selected:hover': {
          backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
        }
      },
      '& .MuiDataGrid-cell': {
        backgroundColor: bgColor,
        color: textColor,
        fontSize: '0.98rem',
        borderBottom: `1px solid ${borderColor}`,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        lineHeight: 'normal',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '8px',
        paddingBottom: '8px'
      },
      '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
        outline: 'none',
      }
    };
  };

  if (isNewExamsWidget) {
    if (loading) {
      return (
        <Box sx={{ width: '100%', height: '100%', minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress size={28} />
          <Typography>{t('approval.loading')}</Typography>
        </Box>
      );
    }
    const newExamsRows = getFilteredAndSortedExams();

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 0.5
        }}
      >
        <Paper
          sx={{
            p: 1,
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1) !important',
            elevation: 0
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zoom: WIDGET_TABLE_ZOOM,
                width: '100%'
              }}
            >
              <DataGrid
                rows={newExamsRows}
                columns={newExamsColumns}
                getRowId={(row) => row.id}
                rowHeight={ROW_HEIGHT}
                headerHeight={HEADER_HEIGHT}
                disableSelectionOnClick
                sx={{ ...getDataGridStyles({ embedWidget: true }) }}
                onRowClick={(params, event) => {
                  if (lockedExams[params.id]) {
                    event.defaultMuiPrevented = true;
                    return;
                  }
                  navigate('/approval', { state: { preselectNewExamId: params.row.id } });
                }}
                sortModel={[{ field: sortBy, sort: sortDirection.toLowerCase() }]}
                onSortModelChange={handleNewExamsSortModelChange}
                columnVisibilityModel={newExamsColumnVisibility}
                onColumnVisibilityModelChange={(newModel) => handleNewExamsColumnVisibilityChange(newModel)}
                localeText={getGridLocale()}
                hideFooter
                isRowSelectable={(params) => !lockedExams[params.id]}
                getRowClassName={(params) => {
                  const classes = [];
                  if (lockedExams[params.id]) classes.push('row-locked');
                  const selId = selectedExam?.id;
                  if (
                    selId != null &&
                    (params.id === selId || String(params.id) === String(selId))
                  ) {
                    classes.push('Mui-selected');
                  }
                  return classes.join(' ');
                }}
                slotProps={{
                  cell: {
                    onMouseEnter: (event) => {
                      const rowId = event.currentTarget.parentElement.getAttribute('data-id');
                      const lockInfo = lockedExams[rowId];
                      if (lockInfo) {
                        event.currentTarget.setAttribute('title', t('dashboard.cards.approval.currentlyOpenedBy', {
                          title: lockInfo.user_name?.split(' ')[0] || '',
                          firstname: lockInfo.user_name?.split(' ')[1] || '',
                          lastname: lockInfo.user_name?.split(' ')[2] || lockInfo.user_name
                        }));
                      }
                    },
                    onMouseLeave: (event) => {
                      event.currentTarget.removeAttribute('title');
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', mt: 4, mb: 4, px: { xs: 2, sm: 3 }, boxSizing: 'border-box' }}>
        <Typography>{t('approval.loading')}</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* VOICE: Versteckter Container für vorgeladene Scanprotokolle (für Sprachsteuerung) */}
      {!scanProtocolDialogOpen && Object.keys(scanProtocolsByRegion).length > 0 && (
        <Box sx={{ 
          position: 'absolute', 
          left: '-9999px', 
          top: '-9999px',
          visibility: 'hidden',
          pointerEvents: 'none'
        }} aria-hidden="true">
          {/* Regionen */}
          {Object.keys(scanProtocolsByRegion).map((region) => (
            <Box
              key={`voice_hidden_region_${region}`}
              id={`voice_region_${region.replace(/\s+/g, '_').toLowerCase()}`}
              data-voice-label={`Region ${region}`}
              data-voice-action="selectRegion"
              data-voice-value={region}
            >
              {region}
            </Box>
          ))}
          {/* Protokolle der ausgewählten Region */}
          {selectedRegion && scanProtocolsByRegion[selectedRegion] && 
            scanProtocolsByRegion[selectedRegion].map((protocol) => (
              <Box
                key={`voice_hidden_protocol_${protocol.id}`}
                id={`voice_protocol_${protocol.id}`}
                data-voice-label={protocol.name}
                data-voice-action="selectProtocol"
                data-voice-value={protocol.name}
              >
                {protocol.name}
              </Box>
            ))
          }
        </Box>
      )}
      
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100%',
        mb: 4,
        px: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 160px)'
      }}>
        <Box
          sx={{
            zoom: APPROVAL_PAGE_CONTENT_ZOOM,
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
      <Box sx={{ 
        display: 'flex', 
        gap: '12px',
        flex: 1,
        width: '100%',
        minHeight: 0,
        overflow: 'hidden',
        mt: 0
      }}>
        {/* Linke Spalte - gleiche Breite wie vorher */}
        <Box sx={{ 
          flex: '1.2', 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Neue Untersuchungen - Obere Hälfte */}
          <Paper sx={{ 
            p: 2, 
            mb: 1.5,
            flex: 1,
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1) !important',
            elevation: 0,
            overflow: 'hidden',
            minHeight: 0
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
              <Typography variant="h6" sx={{ color: 'var(--rd-primary)' }}>
                {t('approval.sections.newExams')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder={t('approval.search.placeholder')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                  }}
                />
                <FormControl size="small" sx={{ 
                  width: '120px',
                  '& .MuiInputLabel-root': {
                    color: statusFilter !== 'all' ? 'var(--rd-primary)' : undefined
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: statusFilter !== 'all' ? 'var(--rd-primary)' : undefined
                    },
                    '&:hover fieldset': {
                      borderColor: statusFilter !== 'all' ? 'var(--rd-primary)' : undefined
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: statusFilter !== 'all' ? 'var(--rd-primary)' : undefined
                    }
                  }
                }}>
                  <InputLabel>{t('approval.filter.status')}</InputLabel>
                  <Select
                    value={statusFilter}
                    label={t('approval.filter.status')}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('approval.filter.all')}</MenuItem>
                    <MenuItem value="NEW">{t('approval.status.new')}</MenuItem>
                    <MenuItem value="APPROVED">{t('approval.status.approved')}</MenuItem>
                    <MenuItem value="REJECTED">{t('approval.status.rejected')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ 
                  width: '120px',
                  '& .MuiInputLabel-root': {
                    color: modalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: modalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                    },
                    '&:hover fieldset': {
                      borderColor: modalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: modalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                    }
                  }
                }}>
                  <InputLabel>{t('approval.filter.modality')}</InputLabel>
                  <Select
                    value={modalityFilter}
                    label={t('approval.filter.modality')}
                    onChange={(e) => setModalityFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('approval.filter.all')}</MenuItem>
                    {[...new Set(newExams.map(exam => exam.modality))].sort().map(modality => (
                      <MenuItem key={modality} value={modality}>{modality}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box>
                  <CustomDatePicker
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    label=""
                    sx={{ 
                      '& .MuiInputBase-root': { 
                        height: '40px',
                        fontSize: '0.875rem',
                        '& input': {
                          padding: '8.5px 14px',
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: '8.5px 14px',
                      }
                    }}
                    InputProps={{
                      endAdornment: dateFilter && (
                        <IconButton
                          size="small"
                          onClick={() => setDateFilter('')}
                          sx={{ mr: -1 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
              <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                <DataGrid
                  rows={getFilteredAndSortedExams()}
                  columns={newExamsColumns}
                  getRowId={(row) => row.id}
                  rowHeight={ROW_HEIGHT}
                  headerHeight={HEADER_HEIGHT}
                  disableSelectionOnClick
                  sx={getDataGridStyles()}
                  onRowClick={(params, event) => {
                    // Blockiere Click wenn Exam gelockt ist
                    if (lockedExams[params.id]) {
                      console.log(`🚫 Click blockiert - Exam ${params.id} ist gelockt von`, lockedExams[params.id].user_name);
                      event.defaultMuiPrevented = true;
                      return;
                    }
                    handleNewExamsRowClick(params, event);
                  }}
                  sortModel={[{ field: sortBy, sort: sortDirection.toLowerCase() }]}
                  onSortModelChange={handleNewExamsSortModelChange}
                  columnVisibilityModel={newExamsColumnVisibility}
                  onColumnVisibilityModelChange={(newModel) => handleNewExamsColumnVisibilityChange(newModel)}
                  localeText={getGridLocale()}
                  hideFooter
                  isRowSelectable={(params) => !lockedExams[params.id]}
                  getRowClassName={(params) => {
                    const classes = [];
                    if (lockedExams[params.id]) classes.push('row-locked');
                    const selId = selectedExam?.id;
                    if (
                      selId != null &&
                      (params.id === selId || String(params.id) === String(selId))
                    ) {
                      classes.push('Mui-selected');
                    }
                    return classes.join(' ');
                  }}
                  slotProps={{
                    cell: {
                      onMouseEnter: (event) => {
                        const rowId = event.currentTarget.parentElement.getAttribute('data-id');
                        const lockInfo = lockedExams[rowId];
                        if (lockInfo) {
                          event.currentTarget.setAttribute('title', t('dashboard.cards.approval.currentlyOpenedBy', {
                            title: lockInfo.user_name?.split(' ')[0] || '',
                            firstname: lockInfo.user_name?.split(' ')[1] || '',
                            lastname: lockInfo.user_name?.split(' ')[2] || lockInfo.user_name
                          }));
                        }
                      },
                      onMouseLeave: (event) => {
                        event.currentTarget.removeAttribute('title');
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Voruntersuchungen - Untere Hälfte */}
          <Paper sx={{ 
            p: showPriorReportText ? 0 : 2, 
            flex: 1,
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1) !important',
            elevation: 0,
            overflow: 'hidden'
          }}>
            {/* Zeige entweder Prior Report Text View oder DataGrid */}
            {showPriorReportText ? (
              <PriorReportTextView
                study={selectedPriorStudy}
                onBack={handleBackFromPriorReportText}
                onShowPdf={handleShowPdfFromTextView}
              />
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h6" sx={{ color: 'var(--rd-primary)' }}>
                      {t('approval.sections.priorStudies')}
                    </Typography>
                    {selectedExam && (
                      <>
                        <Tooltip title={t('aiSummary.button')}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={handleAiSummaryMenuOpen}
                              sx={{
                                color: 'var(--rd-primary)',
                                '&:hover': { backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)' },
                                borderRadius: '8px',
                                px: 0.5
                              }}
                            >
                              <AutoAwesomeIcon fontSize="small" />
                              <ArrowDropDownIcon sx={{ fontSize: 16, ml: -0.3 }} />
                            </IconButton>
                          </Box>
                        </Tooltip>
                        <Menu
                          anchorEl={aiSummaryMenuAnchor}
                          open={Boolean(aiSummaryMenuAnchor)}
                          onClose={handleAiSummaryMenuClose}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                          {aiUserPrompts.map((prompt) => (
                            <MenuItem
                              key={prompt.id}
                              onClick={() => handleAiSummarySelect(prompt)}
                              sx={{ fontSize: '0.85rem' }}
                            >
                              <ListItemIcon>
                                <AutoAwesomeIcon fontSize="small" sx={{ color: prompt.is_default ? 'var(--rd-primary)' : 'text.secondary' }} />
                              </ListItemIcon>
                              <ListItemText>{prompt.name}</ListItemText>
                              {prompt.is_default && (
                                <Chip label="Default" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', backgroundColor: 'var(--rd-primary)', color: 'var(--rd-primary-contrast)' }} />
                              )}
                            </MenuItem>
                          ))}
                          {aiUserPrompts.length > 0 && <Divider />}
                          <MenuItem
                            onClick={() => handleAiSummarySelect(null)}
                            sx={{ fontSize: '0.85rem' }}
                          >
                            <ListItemIcon>
                              <SettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </ListItemIcon>
                            <ListItemText>{t('aiSummary.systemDefault')}</ListItemText>
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </Box>
                  {selectedExam && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder={t('approval.search.placeholder')}
                        value={priorStudiesSearchText}
                        onChange={(e) => setPriorStudiesSearchText(e.target.value)}
                        sx={{ minWidth: 120 }}
                        InputProps={{
                          startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                        }}
                      />
                      <FormControl size="small" sx={{ 
                        width: '120px',
                        '& .MuiInputLabel-root': {
                          color: priorStudiesModalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: priorStudiesModalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                          },
                          '&:hover fieldset': {
                            borderColor: priorStudiesModalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: priorStudiesModalityFilter !== 'all' ? 'var(--rd-primary)' : undefined
                          }
                        }
                      }}>
                        <InputLabel>{t('approval.filter.modality')}</InputLabel>
                        <Select
                          value={priorStudiesModalityFilter}
                          label={t('approval.filter.modality')}
                          onChange={(e) => setPriorStudiesModalityFilter(e.target.value)}
                        >
                          <MenuItem value="all">{t('approval.filter.all')}</MenuItem>
                          {getUniqueModalities().map((modality) => (
                            <MenuItem key={modality} value={modality}>
                              {modality}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flexGrow: 1, overflow: 'hidden', height: '100%' }}>
                  <DataGrid
                  apiRef={priorStudiesApiRef}
                  rows={getFilteredAndSortedPriorStudies()}
                  columns={priorStudiesColumns}
                  getRowId={(row) => row.id || row.study_instance_uid}
                  rowHeight={ROW_HEIGHT}
                  headerHeight={HEADER_HEIGHT}
                  disableColumnMenu={false}
                  disableColumnSorting={true}
                  disableSelectionOnClick
                  hideFooter
                  sx={getDataGridStyles()}
                localeText={getGridLocale()}
                onRowDoubleClick={handlePriorStudiesRowDoubleClick}
                sortingMode="client"
                columnVisibilityModel={priorStudiesColumnVisibility}
                onColumnVisibilityModelChange={handlePriorStudiesColumnVisibilityChange}
                onColumnResize={handlePriorStudiesColumnWidthChange}
                onColumnOrderChange={handlePriorStudiesColumnOrderChange}
                getRowClassName={(params) => 
                  params.row.study_instance_uid ? 'clickable-row' : ''
                }
              />
                </Box>
              </>
            )}
          </Paper>
        </Box>

        {/* Rechte Spalte - optimiert */}
        <Box sx={{ 
          flex: '1.4',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
                    <Paper elevation={0} sx={{ 
            p: 2, 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1) !important',
            overflow: 'hidden' // Verhindert Überlauf
          }}>
            <Box sx={{ 
              height: '100%', 
              overflow: 'auto',
              p: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {!selectedExam ? (
                <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mt: 10 }}>
                  {t('approval.messages.selectExamDetails')}
                </Typography>
              ) : (
                <div>
                <Typography variant="h5" sx={{ mb: 3, color: 'var(--rd-primary)' }}>
                  {t('approval.details.patientData')}
                </Typography>

                {/* Patientendaten Box */}
                <Box sx={{ 
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  p: 2, 
                  borderRadius: 1, 
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ minWidth: '140px', fontWeight: 'bold' }}>{t('approval.details.name')}:</Typography>
                      <Typography>{selectedExam?.patient_name || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ minWidth: '140px', fontWeight: 'bold' }}>{t('approval.details.birthDate')}:</Typography>
                      <Typography>{formatDateWithAge(selectedExam?.birth_date)}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ minWidth: '140px', fontWeight: 'bold' }}>{t('approval.details.patientId')}:</Typography>
                      <Typography>{selectedExam?.patient_id || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ minWidth: '140px', fontWeight: 'bold' }}>{t('approval.details.admissionId')}:</Typography>
                      <Typography>{selectedExam?.admission_id || '-'}</Typography>
                    </Box>
                  </Box>
                  {/* Geschlechts-Symbol */}
                  <Box sx={{ ml: 2, color: 'var(--rd-primary)', display: 'flex', alignItems: 'center' }}>
                    {selectedExam?.sex === 'F' && <Female sx={{ fontSize: '32px', fontWeight: 'bold' }} />}
                    {selectedExam?.sex === 'M' && <Male sx={{ fontSize: '32px', fontWeight: 'bold' }} />}
                    {(selectedExam?.sex === 'O' || selectedExam?.sex === 'U' || selectedExam?.sex === 'D') && 
                      <TransgenderOutlined sx={{ fontSize: '32px', fontWeight: 'bold' }} />}
                  </Box>
                </Box>

                {/* Untersuchungsdetails Box */}
                <Box sx={{ 
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  p: 2, 
                  borderRadius: 1, 
                  mb: 3
                }}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography sx={{ minWidth: '250px', fontWeight: 'bold' }}>{t('approval.details.referringPhysician')}:</Typography>
                    <Typography>{selectedExam?.referring_physician || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography sx={{ minWidth: '250px', fontWeight: 'bold' }}>{t('approval.details.modality')}:</Typography>
                    <Typography>{selectedExam?.modality || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography sx={{ minWidth: '250px', fontWeight: 'bold' }}>{t('approval.details.studyDescription')}:</Typography>
                    <Typography>{selectedExam?.study_description || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ minWidth: '250px', fontWeight: 'bold' }}>{t('approval.details.observationDate')}:</Typography>
                    <Typography>
                      {selectedExam?.observation_date ? (() => {
                        try {
                          const date = new Date(selectedExam.observation_date);
                          return !isNaN(date.getTime()) ? date.toLocaleDateString('de-DE', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric' 
                          }) : '-';
                        } catch {
                          return '-';
                        }
                      })() : '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  {t('approval.details.clinicalInfo')}
                </Typography>
                <Typography sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                  {selectedExam?.clinical_info || '-'}
                </Typography>
                
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  {t('approval.details.question')}
                </Typography>
                <Typography sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                  {selectedExam?.question || '-'}
                </Typography>

                {/* Editierbarer Kommentar */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                    {t('approval.details.comment')}
                  </Typography>
                  <TextField
                    inputRef={commentFieldRef}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    value={editableComment}
                    onChange={(e) => setEditableComment(e.target.value)}
                    onBlur={() => handleSaveField('comment', editableComment)}
                    placeholder={t('approval.details.commentPlaceholder')}
                    variant="outlined"
                    disabled={selectedExam?.status !== 'NEW'}
                    sx={{ backgroundColor: (theme) => theme.palette.background.paper }}
                  />
                </Box>

                {/* Editierbares Untersuchungsprotokoll */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                    {t('approval.details.scanProtocol')}
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      inputRef={protocolFieldRef}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      value={editableScanProtocol}
                      onChange={(e) => setEditableScanProtocol(e.target.value)}
                      onBlur={() => handleSaveField('scan_protocol', editableScanProtocol)}
                      placeholder={t('approval.details.scanProtocolPlaceholder')}
                      variant="outlined"
                      disabled={selectedExam?.status !== 'NEW'}
                      sx={{ backgroundColor: (theme) => theme.palette.background.paper, pr: 6 }}
                    />
                    <IconButton
                      onClick={handleOpenScanProtocolDialog}
                      disabled={selectedExam?.status !== 'NEW'}
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        color: 'primary.main',
                    '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'white'
                        }
                      }}
                      title={t('approval.details.selectPredefinedProtocol')}
                    >
                      <ListIcon />
                    </IconButton>
                  </Box>
                </Box>

                <hr style={{ margin: '20px 0', border: '1px solid var(--rd-divider)' }} />

                <div style={{ textAlign: 'center' }}>
                  <Button
                    className={selectedExam?.status !== 'NEW' ? 'button-disabled' : 'button'}
                    onClick={handleApprove}
                    disabled={selectedExam?.status !== 'NEW'}
                    sx={{ 
                      mr: 2, 
                      minWidth: 140,
                      backgroundColor: selectedExam?.status === 'NEW' ? 'var(--rd-success) !important' : undefined, // Grün für Genehmigen
                      '&:hover': {
                        backgroundColor: selectedExam?.status === 'NEW' ? 'var(--rd-cal-vacation) !important' : undefined
                      }
                    }}
                  >
                    {t('approval.actions.approve')}
                  </Button>
                  <Button
                    className={selectedExam?.status !== 'NEW' ? 'button-disabled' : 'button'}
                    onClick={handleReject}
                    disabled={selectedExam?.status !== 'NEW'}
                    sx={{ 
                      minWidth: 140,
                      backgroundColor: selectedExam?.status === 'NEW' ? 'var(--rd-error) !important' : undefined, // Rot für Ablehnen
                      '&:hover': {
                        backgroundColor: selectedExam?.status === 'NEW' ? 'var(--rd-danger) !important' : undefined
                      }
                    }}
                  >
                    {t('approval.actions.reject')}
                  </Button>
                </div>
                
                {selectedExam?.status !== 'NEW' && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('approval.details.status')}: {getStatusLabel(selectedExam?.status)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 1, 
                        color: 'primary.main', 
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          color: 'primary.dark'
                        }
                      }}
                      onClick={handleOpenStatusChangeDialog}
                    >
                      {t('approval.actions.resetStatus')}
                    </Typography>
                  </Box>
                )}

                {/* Spacer am Ende für sichtbaren weißen Abstand */}
                <Box sx={{ height: '12px', flexShrink: 0 }} />
              </div>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
        </Box>

      {/* Untersuchungsprotokoll Auswahl Dialog */}
      <Dialog
        open={scanProtocolDialogOpen}
        onClose={() => setScanProtocolDialogOpen(false)}
        maxWidth={false}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            '& .MuiPaper-root': {
              position: 'absolute',
              top: 'calc(20% + 100px)',
              right: '140px',
              width: '500px', // Etwas größer für Padding
              maxWidth: 'none',
              margin: 0
            }
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('approval.dialog.scanProtocolsFor')} {selectedExam?.modality}
            </Typography>
            <IconButton onClick={() => setScanProtocolDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
                <DialogContent sx={{ p: 2, height: '400px', overflow: 'hidden' }}>
          <Box sx={{ 
            display: 'flex',
            height: '100%',
            width: '100%'
          }}>
            {/* Regionen - Links */}
            <Box sx={{ 
              width: '180px',
              flexShrink: 0,
              pr: 1
            }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 1, fontSize: '0.9rem' }}>
                {t('approval.dialog.regions')}
              </Typography>
              <Box sx={{ 
                height: 'calc(100% - 30px)',
                overflow: 'auto',
                p: 1
              }}>
                {Object.keys(scanProtocolsByRegion)
                  .sort((a, b) => {
                    // Sortiere Regionen basierend auf dem niedrigsten Index in jeder Region
                    const minIndexA = Math.min(...scanProtocolsByRegion[a].map(p => p.index || 0));
                    const minIndexB = Math.min(...scanProtocolsByRegion[b].map(p => p.index || 0));
                    // Bei gleichem Index: alphabetisch sortieren
                    if (minIndexA === minIndexB) {
                      return a.localeCompare(b);
                    }
                    return minIndexA - minIndexB;
                  })
                  .map((region) => (
                  <Box
                    key={region}
                    id={`voice_region_${region.replace(/\s+/g, '_').toLowerCase()}`}
                    data-voice-label={`Region ${region}`}
                    data-voice-action="selectRegion"
                    data-voice-value={region}
                    onClick={() => setSelectedRegion(region)}
                    sx={{
                      p: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      mb: 0.5,
                      fontSize: '0.8rem',
                      backgroundColor: selectedRegion === region ? 'var(--rd-primary)' : 'transparent',
                      color: selectedRegion === region ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: selectedRegion === region ? 'rgb(224, 132, 0)' : 'grey.100'
                      }
                    }}
                  >
                    {region}
                  </Box>
                ))}
              </Box>
            </Box>
            
            {/* Trennlinie - Mitte */}
            <Box sx={{ 
              width: '20px',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              pb: '2px'
            }}>
              <Box sx={{ 
                width: '2px', 
                backgroundColor: 'var(--rd-primary)',
                height: 'calc(100% - 30px)',
                marginTop: '28px'
              }} />
            </Box>
            
            {/* Protokolle - Rechts */}
            <Box sx={{ 
              flex: 1,
              pl: 1,
              minWidth: 0
            }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 1, fontSize: '0.9rem' }}>
                {t('approval.dialog.protocols')}
              </Typography>
              <Box sx={{ 
                height: 'calc(100% - 30px)',
                overflow: 'auto',
                p: 1
              }}>
                {selectedRegion && scanProtocolsByRegion[selectedRegion] ? 
                  scanProtocolsByRegion[selectedRegion]
                    .sort((a, b) => (a.index || 0) - (b.index || 0))
                    .map((protocol) => (
                    <Box
                      key={protocol.id}
                      id={`voice_protocol_${protocol.id}`}
                      data-voice-label={protocol.name}
                      data-voice-action="selectProtocol"
                      data-voice-value={protocol.name}
                      onClick={() => handleSelectScanProtocol(protocol)}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        borderRadius: 1,
                        mb: 0.5,
                        border: '1px solid var(--rd-divider)',
                        fontSize: '0.8rem',
                        '&:hover': {
                          backgroundColor: 'var(--rd-primary)',
                          color: 'white'
                        }
                      }}
                    >
                      <Box fontWeight="bold">{protocol.name}</Box>
                      <Box sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                        {protocol.description}
                      </Box>
                    </Box>
                  )) : (
                    <Box sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.8rem', mt: 2 }}>
                      {t('approval.dialog.selectRegion')}
                    </Box>
                  )
                }
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Status-Änderungs-Dialog */}
      <Dialog
        open={statusChangeDialogOpen}
        onClose={() => setStatusChangeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('approval.dialog.changeStatus')}
            </Typography>
            <IconButton onClick={() => setStatusChangeDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
              {t('approval.dialog.currentStatus')}: {getStatusLabel(selectedExam?.status)}
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
                              <InputLabel>{t('approval.dialog.newStatus')}</InputLabel>
              <Select
                value={newStatus}
                                  label={t('approval.dialog.newStatus')}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                                  <MenuItem value="NEW">{t('approval.status.new')}</MenuItem>
                  <MenuItem value="APPROVED">{t('approval.status.approved')}</MenuItem>
                  <MenuItem value="REJECTED">{t('approval.status.rejected')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
                              label={t('approval.dialog.reasonLabel')}
              value={statusChangeReason}
              onChange={(e) => setStatusChangeReason(e.target.value)}
                              placeholder={t('approval.dialog.reasonPlaceholder')}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <Typography variant="caption" color="text.secondary">
              {t('approval.dialog.auditNote')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              className="button-cancel"
              onClick={() => setStatusChangeDialogOpen(false)}
            >
              {t('approval.actions.cancel')}
            </Button>
            <Button
              className={!statusChangeReason.trim() ? 'button-disabled' : 'button'}
              onClick={handleSaveStatusChange}
              disabled={!statusChangeReason.trim()}
            >
              {t('approval.actions.changeStatus')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog - Freitext */}
      {pdfPreviewOpen && (
        <PDFPreviewDialog
          open={pdfPreviewOpen}
          onClose={() => {
            setPdfPreviewOpen(false);
            setPdfData(null);
          }}
          pdfData={pdfData}
          reportTitle={t('approval.pdfPreview.title', 'Befund-Vorschau')}
          isPopupWindow={true}
        />
      )}
      
      {/* PDF Preview Dialog - Strukturiert */}
      {pdfPreviewStructuredOpen && (
        <PDFPreviewDialogStructured
          open={pdfPreviewStructuredOpen}
          onClose={() => {
            setPdfPreviewStructuredOpen(false);
            setPdfData(null);
          }}
          pdfData={pdfData}
          reportTitle={t('approval.pdfPreview.titleStructured', 'Strukturierter Befund-Vorschau')}
          isPopupWindow={true}
        />
      )}

      {/* KI Zusammenfassung Popup */}
      <AISummaryDialog
        open={aiSummaryOpen}
        onClose={() => setAiSummaryOpen(false)}
        patientId={selectedExam?.patient_id}
        customPrompt={selectedAiPrompt?.prompt || null}
        promptName={selectedAiPrompt?.name || null}
        examId={selectedExam?.id}
        scope={selectedAiPrompt?.scope || 'full_history'}
      />

      </Box>
    </>
  );
};

export default ApprovalWorkflow; 